// ─── ReportView ───────────────────────────────────────────────────────────────
// Dashboard → Report tab. Four rendering modes, switched by `mode`:
//
//   mode='view'   — show a saved report (chart + stats) for the active id.
//                   Header has Download / Share / Edit.
//   mode='edit'   — same chart + the right-hand Settings sidebar (Report /
//                   Data / View / Filter / Header-Footer / PDF Format
//                   collapsibles). Save persists via PATCH; Cancel exits.
//   mode='list'   — "View All Reports" grid with Open / Delete per card.
//   mode='create' — empty draft + edit sidebar; first Save POSTs to create.
//
// Reports are persisted server-side via reportApi (scoped to project + user)
// so they're never visible to another user or another project.
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Line, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts'
import { ChartHoverTooltip } from './ChartHoverTooltip'
import { ChevronDown, Edit2, Download, Share2, Upload, Save, X, Loader, AlertCircle, Check } from 'lucide-react'
import {
    listReports, createReport, getReport, updateReport, deleteReport,
    exportReportGlos, exportReportNdbc, listNdbcTags,
    shareReportEmail, shareReportFtp,
    createSchedule, listSchedules, updateSchedule, deleteSchedule,
} from '../../lib/reportApi'
import { projectsApi } from '../../lib/projectsApi'
import useAuthStore from '../../store/auth-store'
import {
  resolveIanaTz, makeTimeAxisFormatter, formatMediaTimestamp,
  wallClockPartsInTz, parseWallClockInTzToMs, monthStartsInRange, monthTickLabels,
  halfDayTicksInRange, halfDayTickLabels, dailyTicksInRange, dayMonthTickLabels,
  twoHourTicksInRange, twoHourTickLabels,
} from '../../lib/tz'
import ReportScheduleListModal from './ReportScheduleListModal'
import ScheduleForm, { emptyScheduleValue } from './ScheduleForm'

const CHART_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4']

const fmtRange = (from, to) => {
  const f = from ? new Date(from) : null
  const t = to   ? new Date(to)   : null
  const fmt = (d) => d ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'
  return `${fmt(f)} - ${fmt(t)}`
}

// Editor state shape. Persisted as Report.settings JSON server-side so
// adding fields here is backward-compatible — older saved reports just
// fall back to defaults when a key is missing.
const DEFAULT_SETTINGS = {
  // ── Report section ─────────────────────────────────────────────────────
  includeTimestamp: false,
  description: '',
  timezone:    '',
  showErrorValue: 'No',
  // ── Data section ───────────────────────────────────────────────────────
  parameters: [],
  dateRange:  'Today',                  // preset: Today / Yesterday / Last 7 Days / …
  dateFrom:   '',                       // YYYY-MM-DD HH:mm — populated when dateRange='Custom'
  dateTo:     '',
  condenseMode: 'None',                 // None / Hourly / Daily / Weekly / Monthly
  // ── View section ───────────────────────────────────────────────────────
  view: {
    header:    true,
    statTable: true,
    plot:      true,
    plotMode:  'Combine Plots',         // Combine Plots / Separate Plots
    dataTable: true,
  },
  // ── Filter section ─────────────────────────────────────────────────────
  // `filters` is the canonical array shape; the legacy single-object
  // `filter` stays in saved reports until they're re-saved.
  filters: [],
  // ── Header / Footer section ────────────────────────────────────────────
  headerFooter: {
    topTitle:     { enabled: true, value: '' },
    reportTitle:  { enabled: true, value: '' },
    subtitle:     { enabled: true, value: '' },
    datePrepared: { enabled: true, value: '' },
    timePrepared: { enabled: true, value: '12-hour' },
    // Footer side — two rows per the design, both with their own
    // enable-toggle + text. `pageNumber` defaults on with the conventional
    // template; `extraFooter` is the optional free-form line.
    pageNumber:   { enabled: true,  value: 'Page 1 of 2' },
    extraFooter:  { enabled: false, value: '' },
  },
  // ── Chart section ──────────────────────────────────────────────────────
  // `charts` is the canonical array (each entry → its own chart block in
  // the rendered report). `chart` keeps global rendering options
  // (Line/Area/Bar, smoothing, grid, legend, dot markers) that apply to
  // every chart. Each charts entry: { title, parameterIds: [number...] }
  // with at most 4 parameterIds.
  charts: [],
  chart: {
    type:       'Line',     // Line / Area / Bar
    smooth:     false,   // charts join measured points straight; smoothing is opt-in
    showDots:   false,
    showGrid:   true,
    showLegend: true,
  },
  // ── Y-Axis section ─────────────────────────────────────────────────────
  yAxis: {
    autoScale:   true,
    min:         '',
    max:         '',
    showUnit:    true,
    logarithmic: false,
  },
  // ── PDF Format section ─────────────────────────────────────────────────
  pdfFormat: { orientation: 'Portrait', paperSize: 'Letter (8.5 x 11 in)' },
}

const DATE_RANGE_PRESETS = [
  'Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days', 'This Month',
  'Last Month', 'This Year', 'Last Year', 'Custom',
]
const CONDENSE_MODES = ['None', 'Hourly', 'Daily', 'Weekly', 'Monthly']

// True when the report's From→To span exceeds one day. Individual data-point
// markers turn into unreadable clutter over longer ranges, so both the "Show
// data points" toggle and the chart dots themselves are suppressed past 24h.
// Accepts the 'YYYY-MM-DDTHH:mm' / 'YYYY-MM-DD HH:mm' strings used for the
// report's dateFrom/dateTo.
function spanExceedsOneDay(from, to) {
  if (!from || !to) return false
  const f = new Date(String(from).replace(' ', 'T')).getTime()
  const t = new Date(String(to).replace(' ', 'T')).getTime()
  if (isNaN(f) || isNaN(t)) return false
  return (t - f) > 24 * 60 * 60 * 1000
}

// Whether a report config's effective range spans more than a day. Uses the
// explicit dateFrom/dateTo when present, otherwise resolves the preset range
// name (a saved report may only carry its preset, not the from/to). So
// multi-day presets (Last 7 Days, This Month, …) correctly count as > 1 day.
function reportSpanOverOneDay(cfg) {
  if (!cfg) return false
  let from = cfg.dateFrom
  let to = cfg.dateTo
  if ((!from || !to) && cfg.dateRange && cfg.dateRange !== 'Custom') {
    const r = rangeFromPreset(cfg.dateRange, resolveIanaTz(cfg.timezone))
    if (r) { from = r.from; to = r.to }
  }
  return spanExceedsOneDay(from, to)
}

// Resolve a preset name into a { from, to } pair of 'YYYY-MM-DDTHH:mm'
// strings (the format <input type="datetime-local"> consumes). Returns
// null for 'Custom' so the user's manually-edited values stay intact.
// `tz` is the report's timezone (its own Timezone setting, else the account's).
// The window MUST be built from that zone's calendar, because the From/To
// strings are sent to the API to be interpreted in it. They used to be built
// from `new Date()` + setHours/getFullYear — all browser-local — so a viewer in
// a different zone got the wrong window: for an Eastern report opened from
// India, "Today" resolved to Aug 13 00:00–23:59 IST, i.e. Aug 12 14:30 → Aug 13
// 14:29 Eastern. Close to midnight the DATE itself was off by a day, and the
// same skew moved every month/year boundary.
//
// Arithmetic is done on the zone's civil date via Date.UTC, so day/month
// stepping can't be perturbed by DST in either zone.
function rangeFromPreset(preset, tz) {
  const pad = (n) => String(n).padStart(2, '0')
  const civil = (y, mZeroBased, d) => new Date(Date.UTC(y, mZeroBased, d))
  const at = (dt, hh, mm) =>
    `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}T${pad(hh)}:${pad(mm)}`
  const startOf = (dt) => at(dt, 0, 0)
  const endOf   = (dt) => at(dt, 23, 59)

  // "Now" as wall-clock in the report's timezone.
  const p = wallClockPartsInTz(new Date(), tz || 'UTC')
  const today = civil(p.year, p.month - 1, p.day)
  const shiftDays = (dt, n) => civil(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate() + n)

  switch (preset) {
    case 'Today':         return { from: startOf(today), to: endOf(today) }
    case 'Yesterday': {
      const y = shiftDays(today, -1)
      return { from: startOf(y), to: endOf(y) }
    }
    case 'Last 7 Days':
      return { from: startOf(shiftDays(today, -6)), to: endOf(today) }
    case 'Last 30 Days':
      return { from: startOf(shiftDays(today, -29)), to: endOf(today) }
    case 'This Month':
      return { from: startOf(civil(p.year, p.month - 1, 1)), to: endOf(today) }
    case 'Last Month': {
      const s = civil(p.year, p.month - 2, 1)   // month-2 handles January → December
      const e = civil(p.year, p.month - 1, 0)   // day 0 = last day of previous month
      return { from: startOf(s), to: endOf(e) }
    }
    case 'This Year':
      return { from: startOf(civil(p.year, 0, 1)), to: endOf(today) }
    case 'Last Year':
      return { from: startOf(civil(p.year - 1, 0, 1)), to: endOf(civil(p.year - 1, 11, 31)) }
    case 'Custom':
    default:              return null
  }
}
const TIMEZONE_OPTIONS = [
  '(UTC-12:00) International Date Line West',
  '(UTC-10:00) Hawaii',
  '(UTC-08:00) Pacific Time (US & Canada)',
  '(UTC-07:00) Mountain Time (US & Canada)',
  '(UTC-06:00) Central Time (US & Canada)',
  '(UTC-05:00) Eastern Time (US & Canada)',
  '(UTC-04:00) America/New_York',
  '(UTC+00:00) UTC',
  '(UTC+01:00) London, Paris, Berlin',
  '(UTC+05:30) India Standard Time',
]

// ─── Condense (Hourly / Daily / Weekly / Monthly) ─────────────────────────────
// Bucket raw points by time, averaging y within each bucket. The chart, stats
// table, and data table all consume the condensed result, so a Daily report
// shows ~one point per day regardless of the device's sample rate.
//
// Weekly buckets start on Monday to match standard ISO weeks; Monthly buckets
// start on the 1st of the month. Bucket x is the bucket-start timestamp so
// consecutive buckets line up cleanly on the chart x-axis.
function condensePoints(points, mode) {
  if (!Array.isArray(points) || points.length === 0 || !mode || mode === 'None') return points
  const bucketStart = (ms) => {
    const d = new Date(ms)
    if (mode === 'Hourly') {
      d.setMinutes(0, 0, 0); return d.getTime()
    }
    if (mode === 'Daily') {
      d.setHours(0, 0, 0, 0); return d.getTime()
    }
    if (mode === 'Weekly') {
      d.setHours(0, 0, 0, 0)
      // JS Sunday=0, Monday=1 — shift back to Monday.
      const day = d.getDay()
      const back = day === 0 ? 6 : day - 1
      d.setDate(d.getDate() - back)
      return d.getTime()
    }
    if (mode === 'Monthly') {
      return new Date(d.getFullYear(), d.getMonth(), 1).getTime()
    }
    return ms
  }
  const buckets = new Map() // bucketStart -> { sum, count }
  for (const pt of points) {
    if (pt.x == null || pt.y == null) continue
    const x = typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x)
    const y = Number(pt.y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue
    const key = bucketStart(x)
    const cur = buckets.get(key) || { sum: 0, count: 0 }
    cur.sum += y
    cur.count += 1
    buckets.set(key, cur)
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([x, { sum, count }]) => ({ x, y: sum / count }))
}

// ─── CSV export — Data Table only ─────────────────────────────────────────────
// Mirrors the on-screen Data Table layout: one block per device, with a
// Site/Device/Sensor banner then a Timestamp + per-parameter column header,
// followed by one row per timestamp. Blocks are separated by a blank line.
function buildDataTableCsv(params, data, customerTz, settings) {
  // Same condense/filter/error-value transform the on-screen table uses, so a
  // downloaded (or shared) CSV matches what the user sees.
  const eff = computeEffectiveData(data, settings)
  const fmtTs = (x) => {
    const d = new Date(x)
    if (isNaN(d.getTime())) return String(x)
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: customerTz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).format(d).replace(',', '')
    } catch { return d.toISOString() }
  }
  const esc = (v) => {
    if (v == null) return ''
    const s = String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }

  const groups = new Map()
  for (const p of params) {
    const key = p.deviceName || `__nodev_${p.id}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(p)
  }

  const lines = []
  for (const [, devParams] of groups) {
    const head = devParams[0] || {}
    lines.push(`Site,${esc(head.siteName || '')}`)
    lines.push(`Device,${esc(head.deviceName || '')}`)
    lines.push(`Sensor,${esc(Array.from(new Set(devParams.map(p => p.sensorName).filter(Boolean))).join('; '))}`)
    const header = ['Timestamp', ...devParams.map(p => `${p.name}${p.unit ? ` (${p.unit})` : ''}`)]
    lines.push(header.map(esc).join(','))

    const tsMap = new Map()
    for (const p of devParams) {
      for (const pt of (eff[p.id]?.points || [])) {
        if (pt.x == null) continue
        if (pt.y == null && pt.err == null) continue
        const x = typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x)
        if (!tsMap.has(x)) tsMap.set(x, {})
        tsMap.get(x)[p.id] = pt
      }
    }
    const rows = Array.from(tsMap.entries()).sort((a, b) => b[0] - a[0])
    for (const [x, byId] of rows) {
      const cells = [fmtTs(x), ...devParams.map(p => {
        const v = tableCellValue(byId[p.id])
        return typeof v === 'number' ? v.toFixed(2) : (v ?? '')
      })]
      lines.push(cells.map(esc).join(','))
    }
    lines.push('') // blank line between devices
  }
  return lines.join('\n')
}

// ─── PDF export — 3-page report ──────────────────────────────────────────────
// Produces:
//   Page 1 — Header (Top Title / Report Title / Subtitle) + Statistical Summary
//   Page 2 — "Plots" + captured chart image
//   Page 3 — Data Table (Site / Device / Sensor / Parameter header rows, then
//            one row per timestamp with values across devices)
// Uses jsPDF + jspdf-autotable; the chart image is captured from the on-screen
// report container via html2canvas (chart must be visible when triggered).
async function buildReportPdf({ settings, params, data, customerTz, filename, returnBlob = false }) {
  if (!params.length) throw new Error('No parameters selected.')
  // Condense/filter/error-value transform — identical to the on-screen table,
  // so the PDF's stats + data table match what the user sees (and match the CSV).
  const eff = computeEffectiveData(data, settings)
  const jsPDFmod    = await import('jspdf')
  const html2canvas = (await import('html2canvas')).default
  // jspdf-autotable v5 exposes a named `autoTable(doc, options)` function
  // (jspdf v4 no longer accepts prototype patching from v3 of the plugin).
  const { autoTable } = await import('jspdf-autotable')

  const { jsPDF } = jsPDFmod
  const pdfFmt     = settings?.pdfFormat || {}
  const pdfOrient  = pdfFmt.orientation === 'Landscape' ? 'landscape' : 'portrait'
  const pdfSizeRaw = pdfFmt.paperSize || 'Letter (8.5 x 11 in)'
  const pdfSize    = pdfSizeRaw.startsWith('A4')      ? 'a4'
                   : pdfSizeRaw.startsWith('Legal')   ? 'legal'
                   : pdfSizeRaw.startsWith('Tabloid') ? 'tabloid'
                   : 'letter'
  const doc  = new jsPDF({ orientation: pdfOrient, unit: 'pt', format: pdfSize })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const MARGIN = 36

  // ── Helpers ────────────────────────────────────────────────────────────────
  const hf = settings?.headerFooter || {}
  const get = (k) => hf?.[k] || { enabled: true, value: '' }
  const now = new Date()
  const fmtDate = (d) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz, year: 'numeric', month: 'long', day: 'numeric',
      }).format(d)
    } catch { return d.toDateString() }
  }
  const fmtTime = (d) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: true,
      }).format(d)
    } catch { return d.toLocaleTimeString() }
  }
  const fmtStamp = (x) => {
    const d = new Date(x)
    if (isNaN(d.getTime())) return String(x)
    try {
      // "MM-DD-YYYY HH:MM:SS" — matches the reference layout.
      const s = new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).format(d)
      // en-US puts date as "MM/DD/YYYY, HH:MM:SS"; normalise to
      // "MM-DD-YYYY HH:MM:SS".
      return s.replace(/^(\d{2})\/(\d{2})\/(\d{4}),?\s*/, '$1-$2-$3 ')
    } catch { return d.toISOString().slice(0, 19).replace('T', ' ') }
  }
  const num = (v, digits = 2) =>
    (v == null || !Number.isFinite(Number(v))) ? '—' : Number(v).toFixed(digits)

  // ── Page 1: Header + Statistical Summary ──────────────────────────────────
  //
  // Top row: date on the left, time below it. Titles centered.
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(64)
  doc.text(fmtDate(now), MARGIN, MARGIN + 4)
  doc.text(fmtTime(now), MARGIN, MARGIN + 16)

  let y = MARGIN + 4
  const topTitle    = get('topTitle').enabled    ? (get('topTitle').value    || '') : ''
  const reportTitle = get('reportTitle').enabled ? (get('reportTitle').value || settings?.name || 'Report') : (settings?.name || 'Report')
  const subtitle    = get('subtitle').enabled    ? (get('subtitle').value    || '') : ''
  if (topTitle) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(30)
    doc.text(topTitle, pageW / 2, y, { align: 'center' })
    y += 20
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20)
  doc.text(reportTitle, pageW / 2, y + 4, { align: 'center' })
  y += 24
  if (subtitle) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(80)
    doc.text(subtitle, pageW / 2, y, { align: 'center' })
    y += 18
  }

  // Statistical Summary heading
  y += 18
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20)
  doc.text('Statistical Summary', pageW / 2, y, { align: 'center' })
  y += 10

  // Compute Min/Max timestamps by scanning each parameter's points.
  const statsRows = params.map((p) => {
    const pts = (eff[p.id]?.points) || []
    let min = Infinity, minAt = null, max = -Infinity, maxAt = null
    let sum = 0, n = 0
    const values = []
    for (const pt of pts) {
      const v = Number(pt.y)
      if (!Number.isFinite(v)) continue
      values.push(v)
      if (v < min) { min = v; minAt = pt.x }
      if (v > max) { max = v; maxAt = pt.x }
      sum += v; n += 1
    }
    if (!n) {
      return {
        p, count: 0, min: null, minAt: null, max: null, maxAt: null,
        mean: null, median: null, std: null,
      }
    }
    const mean = sum / n
    const sorted = [...values].sort((a, b) => a - b)
    const median = n % 2 === 0 ? (sorted[n/2-1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)]
    const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n
    return {
      p, count: n, min, minAt, max, maxAt, mean, median, std: Math.sqrt(variance),
    }
  })

  // autoTable — Sites, Device, Sensors, Parameters, Samples, Min, Max, Mean, Median, Std
  autoTable(doc, {
    startY: y + 6,
    margin: { left: MARGIN, right: MARGIN },
    styles:   { fontSize: 8, cellPadding: 3, valign: 'top', overflow: 'linebreak', textColor: 30 },
    headStyles: { fillColor: [245, 245, 245], textColor: 40, halign: 'center', fontStyle: 'bold' },
    theme: 'grid',
    head: [[
      'Sites', 'Device', 'Sensors', 'Parameters',
      'Samples', 'Min', 'Max', 'Mean', 'Median', 'Std',
    ]],
    body: statsRows.map((r) => [
      r.p.siteName   || '—',
      r.p.deviceName || '—',
      r.p.sensorName || '—',
      `${r.p.name || ''}${r.p.unit ? ` (${r.p.unit})` : ''}`,
      String(r.count ?? 0),
      r.min != null ? `${num(r.min)}\n${fmtStamp(r.minAt)}` : '—',
      r.max != null ? `${num(r.max)}\n${fmtStamp(r.maxAt)}` : '—',
      num(r.mean),
      num(r.median),
      num(r.std),
    ]),
  })

  // ── Page 2: Plots ─────────────────────────────────────────────────────────
  doc.addPage()
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20)
  doc.text('Plots', pageW / 2, MARGIN + 10, { align: 'center' })
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(70)
  doc.text('Selected Parameters', pageW / 2, MARGIN + 30, { align: 'center' })

  // Grab the on-screen chart. We look for a Recharts ResponsiveContainer inside
  // the printable report region — that's the chart the ReportContent renders.
  const chartEl = document.querySelector('.report-root .recharts-responsive-container')
    || document.querySelector('.report-root .recharts-wrapper')
  if (chartEl) {
    try {
      const canvas = await html2canvas(chartEl, {
        scale: 2,
        backgroundColor: '#fff',
        useCORS: true,
      })
      const img = canvas.toDataURL('image/png')
      const availW = pageW - MARGIN * 2
      const iw = availW
      const ih = canvas.height * (iw / canvas.width)
      const cappedIh = Math.min(ih, pageH - MARGIN * 2 - 60)
      const cappedIw = cappedIh < ih ? canvas.width * (cappedIh / canvas.height) : iw
      const xOffset = MARGIN + (availW - cappedIw) / 2
      doc.addImage(img, 'PNG', xOffset, MARGIN + 50, cappedIw, cappedIh)
    } catch (_) {
      doc.setFontSize(10); doc.setTextColor(120)
      doc.text('Chart could not be rendered.', pageW / 2, pageH / 2, { align: 'center' })
    }
  } else {
    doc.setFontSize(10); doc.setTextColor(120)
    doc.text('Chart is not currently visible.', pageW / 2, pageH / 2, { align: 'center' })
  }

  // ── Page 3: Data Table ────────────────────────────────────────────────────
  doc.addPage()
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20)
  doc.text('Data Table', pageW / 2, MARGIN + 10, { align: 'center' })

  // Build a single wide table: first column is Time, one column per parameter.
  // The first four "header" body rows carry Site / Device / Sensor / Parameter
  // labels so each column is fully qualified without needing a second header.
  const tzLabel = (() => {
    try {
      const opt = new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz, timeZoneName: 'longOffset',
      }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || ''
      return `Time ${customerTz} ${opt || ''}`.trim()
    } catch { return 'Time' }
  })()

  const paramColHead = params.map(() => '')
  const timeCol = tzLabel
  const bodyHeaderRows = [
    ['Site',      ...params.map((p) => p.siteName   || '—')],
    ['Device',    ...params.map((p) => p.deviceName || '—')],
    ['Sensor',    ...params.map((p) => p.sensorName || '—')],
    ['Parameter', ...params.map((p) => p.name       || '')],
    ['',          ...params.map((p) => p.unit       || '')],
  ]

  // Timestamps: union across all parameters, sorted ascending.
  const tsMap = new Map()
  for (const p of params) {
    for (const pt of (eff[p.id]?.points) || []) {
      if (pt.x == null) continue
      if (pt.y == null && pt.err == null) continue
      const x = typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x)
      if (!tsMap.has(x)) tsMap.set(x, {})
      tsMap.get(x)[p.id] = pt
    }
  }
  const rows = Array.from(tsMap.entries()).sort((a, b) => a[0] - b[0])
  const dataBody = rows.map(([x, byId]) => [
    fmtStamp(x),
    ...params.map((p) => {
      const v = tableCellValue(byId[p.id])
      return typeof v === 'number' ? v.toFixed(2) : (v ?? '—')
    }),
  ])

  autoTable(doc, {
    startY: MARGIN + 26,
    margin: { left: MARGIN, right: MARGIN, top: MARGIN + 26, bottom: MARGIN },
    styles: { fontSize: 8, cellPadding: 2.5, textColor: 40, halign: 'right' },
    headStyles: { fillColor: [245, 245, 245], textColor: 30, halign: 'center', fontStyle: 'bold' },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
    theme: 'grid',
    head: [[timeCol, ...paramColHead]],
    body: [...bodyHeaderRows, ...dataBody],
  })

  // Share reuses the exact same document — return the bytes instead of
  // triggering a browser download so the emailed / FTP'd file is identical.
  if (returnBlob) return doc.output('blob')
  doc.save(filename)
}

function downloadBlob(filename, content, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mime })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Read a Blob as raw base64 (no `data:…;base64,` prefix) for JSON transport.
// Used by Share so the backend can attach/upload the exact bytes the browser
// built for Download.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error('read failed'))
    reader.onload  = () => {
      const s = String(reader.result || '')
      const comma = s.indexOf(',')
      resolve(comma >= 0 ? s.slice(comma + 1) : s)
    }
    reader.readAsDataURL(blob)
  })
}

function computeStats(values) {
  if (!values.length) return { count: 0 }
  const n = values.length
  const sorted = [...values].sort((a, b) => a - b)
  const sum = values.reduce((a, b) => a + b, 0)
  const mean = sum / n
  const median = n % 2 === 0 ? (sorted[n/2-1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)]
  const variance = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n
  return { count: n, min: sorted[0], max: sorted[n-1], mean, median, std: Math.sqrt(variance) }
}

// ─── Effective data — condense + filter + error-value, then re-stat ───────────
// Single source of truth for what a report "shows": raw fetched points run
// through condense (Hourly/Daily/…), the min/max filters, and the Show Error
// Value toggle, with stats recomputed from the survivors. The on-screen view
// (ReportContent) AND the exports (buildReportPdf / buildDataTableCsv) all call
// this so the chart, the downloaded PDF/CSV, and the emailed/FTP'd copy show
// identical rows and identical stats. Keep this the ONLY place the transform
// lives — divergence here is exactly what made Download and Share disagree.
function computeEffectiveData(data, settings) {
  const condenseMode   = settings?.condenseMode || 'None'
  const showErrorValue = settings?.showErrorValue === 'Yes'
  const filtersRaw = Array.isArray(settings?.filters) && settings.filters.length
    ? settings.filters
    : (settings?.filter && (settings.filter.parameterId || settings.filter.min || settings.filter.max)
        ? [settings.filter]
        : [])
  const rules = filtersRaw.map((f) => {
    const min = f.min === '' || f.min == null ? -Infinity : Number(f.min)
    const max = f.max === '' || f.max == null ?  Infinity : Number(f.max)
    return {
      targetId: f.parameterId ? String(f.parameterId) : null,
      min, max,
      active: Number.isFinite(min) || Number.isFinite(max),
    }
  }).filter((r) => r.active)

  const out = {}
  for (const key of Object.keys(data || {})) {
    const ent = data[key] || {}
    // 1) Condense first — bucket means use every sample in range, not just
    // the post-filter ones.
    let pts = condensePoints(ent.points || [], condenseMode)
    // An error marker carries a raw `err` reading with its y already nulled
    // upstream (graph-data). It is NOT a real sample: it bypasses the numeric
    // Min/Max filters below and never feeds the chart or stats.
    const isErr = (pt) => pt && pt.err != null
    // 2) Apply every rule that targets this series (untargeted rules apply to
    // all). Rules combine with AND. Nulls / error markers pass through here and
    // are resolved in step 4.
    const matchingRules = rules.filter((r) => !r.targetId || r.targetId === String(key))
    if (matchingRules.length) {
      pts = pts.filter(pt => {
        if (pt.y == null) return true
        const y = Number(pt.y)
        if (!Number.isFinite(y)) return false
        for (const r of matchingRules) {
          if (Number.isFinite(r.min) && y < r.min) return false
          if (Number.isFinite(r.max) && y > r.max) return false
        }
        return true
      })
    }
    // 3) Stats use ONLY real readings — error markers and nulls are excluded.
    const ys = pts.filter(pt => pt.y != null).map(pt => Number(pt.y)).filter(v => Number.isFinite(v))
    // Chart series keeps the gap nulls (hidden-status readings come back from
    // graph-data as y:null, plus error markers) so the line BREAKS at the hidden
    // section (chart draws with connectNulls={false}). The data table / stats
    // below still drop pure nulls so they don't show blank rows.
    const chartPoints = pts
    // 4) Emit the data-table series: real readings always; error markers only
    // when Show Error Value = Yes (the table renders their raw `err`). Range
    // bookends / condense gaps (pure nulls) are dropped from the TABLE here.
    pts = pts.filter(pt => pt.y != null || (showErrorValue && isErr(pt)))
    out[key] = { points: pts, chartPoints, stats: computeStats(ys) }
  }
  return out
}

// Value a report data-table cell shows for one point. Real readings show their
// number; an error marker shows its raw `err` reading. Error markers only reach
// the table at all when Show Error Value = Yes (computeEffectiveData drops them
// otherwise), so their presence alone means "show it". Returns undefined for an
// absent/empty cell so callers can render their own placeholder ('—' / '').
function tableCellValue(pt) {
  if (pt == null) return undefined
  if (pt.y != null) return pt.y
  if (pt.err != null) return pt.err
  return undefined
}

function mergePoints(data, params, keepNulls = false, field = 'points') {
  const map = {}
  for (const p of params) {
    const pts = data[p.id]?.[field] || []
    for (const pt of pts) {
      if (pt.x == null) continue
      if (!keepNulls && pt.y == null) continue
      const x = typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x)
      if (!map[x]) map[x] = { x }
      map[x][`p_${p.id}`] = pt.y ?? null
    }
  }
  return Object.values(map).sort((a, b) => a.x - b.x)
}

// ─── Sidebar collapsible section ──────────────────────────────────────────────
// `searchQuery` lets the sidebar filter sections without each section having
// to know about it. When the query is non-empty:
//   • sections whose title doesn't match are hidden entirely
//   • matching sections are auto-expanded so the user can see why they matched
//   • passing `keywords` (an array of strings) lets a section ALSO match on
//     terms that aren't in its title (e.g. "From" / "To" → Data section)
function SettingsSection({ title, keywords = [], children, defaultOpen = false, searchQuery = '' }) {
  const [open, setOpen] = useState(defaultOpen)
  const q = (searchQuery || '').trim().toLowerCase()
  const matches = !q
    || title.toLowerCase().includes(q)
    || keywords.some((k) => String(k).toLowerCase().includes(q))
  if (!matches) return null
  const effectiveOpen = q ? true : open
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="common-size18 w-full flex items-center justify-between py-3 text-sm font-bold text-[#4D4D4D] hover:text-[#358ECD]"
      >
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${effectiveOpen ? 'rotate-180' : ''}`} />
      </button>
      {effectiveOpen && <div className="pb-3 space-y-3">{children}</div>}
    </div>
  )
}

// ─── Edit-mode sidebar ────────────────────────────────────────────────────────
// Six collapsible sections matching the XD reference exactly:
//   Report          — name, timestamp toggle, description, tz, error-value
//   Data            — date-range preset, From/To, condense mode
//   View            — Include Sections checklist (Header / Stat Table / Plot
//                     [Combine|Separate] / Data Table)
//   Filter          — per-parameter Min/Max filter
//   Header/Footer   — top title / report title / subtitle / date / time
//   PDF Format      — orientation + paper size
function SettingsSidebar({ draft, setDraft, sidebarParameters = [], searchQuery, setSearchQuery }) {
  // Date Range presets resolve against the report's own timezone (its Timezone
  // setting, else the signed-in account's) — not the browser's. See
  // rangeFromPreset().
  const accountTz = useAuthStore(s => s.user?.timezone)
  const presetTz = (() => {
    const t = (draft?.timezone || '').trim()
    return resolveIanaTz(!t || t === 'Use account timezone' ? accountTz : t)
  })()
  const updateField  = (key, value) => setDraft(d => ({ ...d, [key]: value }))
  const updateNested = (group, key, value) =>
    setDraft(d => ({ ...d, [group]: { ...(d[group] || {}), [key]: value } }))
  // headerFooter rows are { enabled, value } pairs — small helper so the
  // markup stays readable.
  const updateHF = (key, patch) =>
    setDraft(d => ({
      ...d,
      headerFooter: { ...(d.headerFooter || {}), [key]: { ...(d.headerFooter?.[key] || {}), ...patch } },
    }))

  // Source list comes from the dashboard's LEFT sidebar — only the
  // parameters the user has checked there are offered in the report.
  // Carry over the same shape used elsewhere so the rest of the file
  // doesn't have to change.
  const allParams = useMemo(() => (
    (sidebarParameters || []).map((p) => ({
      id:         p.id,
      name:       p.name,
      unit:       p.unit || '',
      siteName:   p.siteName || p.site_name || '',
      deviceName: p.deviceName || p.device_name || '',
      sensorName: p.sensorName || p.sensor_name || '',
    }))
  ), [sidebarParameters])

  const toggleParameter = (param) => {
    setDraft(d => {
      const existing = (d.parameters || []).find(p => p.id === param.id)
      const next = existing
        ? d.parameters.filter(p => p.id !== param.id)
        : [...(d.parameters || []), param]
      return { ...d, parameters: next }
    })
  }
  const selectedIds = new Set((draft.parameters || []).map(p => p.id))

  const labelCls = 'common-size15 block text-xs font-semibold text-[#4D4D4D mb-1]'
  const fieldCls = 'mt-1 w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]'
  const inputCls = 'w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#358ECD]'

  return (
    <aside className="w-72 bg-white border-gray-200 rounded-lg p-6 self-start max-[767px]:w-full">
      <h3 className="common-head25 text-lg font-bold text-[#4D4D4D] mb-2">Settings</h3>
      <input
        type="text"
        placeholder="Search Settings"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className={`${inputCls} mb-3`}
      />

      {/* ── Report ────────────────────────────────────────────────────── */}
      <SettingsSection
        title="Report"
        keywords={['Name', 'Timestamp', 'Description', 'Timezone', 'Error Value']}
        searchQuery={searchQuery}
      >
        <label className="block">
          <span className={labelCls}>Report Name</span>
          <input
            type="text"
            value={draft.name || ''}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Report 1"
            className={fieldCls}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input
            type="checkbox"
            checked={!!draft.includeTimestamp}
            onChange={(e) => updateField('includeTimestamp', e.target.checked)}
          />
          Include Timestamp in Report Name
        </label>
        <label className="block">
          <span className={labelCls}>Description</span>
          <textarea
            rows={3}
            value={draft.description || ''}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Report description"
            className={`${fieldCls} resize-y`}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Timezone</span>
          <select
            value={draft.timezone || ''}
            onChange={(e) => updateField('timezone', e.target.value)}
            className={`${fieldCls} bg-white`}
          >
            <option value="">Use account timezone</option>
            {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>Show Error Value</span>
          <select
            value={draft.showErrorValue || 'No'}
            onChange={(e) => updateField('showErrorValue', e.target.value)}
            className={`${fieldCls} bg-white`}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </label>
      </SettingsSection>

      {/* ── Time Range ────────────────────────────────────────────────── */}
      <SettingsSection
        title="Time Range"
        keywords={['Date Range', 'Data', 'From', 'To', 'Condense Mode', 'Parameters']}
        searchQuery={searchQuery}
      >
        <label className="block">
          <span className={labelCls}>Date Range</span>
          <select
            value={draft.dateRange || 'Today'}
            onChange={(e) => {
              const next = e.target.value
              const range = rangeFromPreset(next, presetTz)
              setDraft(d => ({
                ...d,
                dateRange: next,
                ...(range ? { dateFrom: range.from, dateTo: range.to } : {}),
              }))
            }}
            className={`${fieldCls} bg-white`}
          >
            {DATE_RANGE_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        {/* From / To always shown — the preset adjusts the values, the user
            can still tweak them by hand. Manual edits flip dateRange to
            'Custom' so the dropdown matches what's in the inputs. */}
        <label className="block">
          <span className={labelCls}>From</span>
          <input
            type="datetime-local"
            value={draft.dateFrom || ''}
            onChange={(e) => setDraft(d => ({ ...d, dateFrom: e.target.value, dateRange: 'Custom' }))}
            className={fieldCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>To</span>
          <input
            type="datetime-local"
            value={draft.dateTo || ''}
            onChange={(e) => setDraft(d => ({ ...d, dateTo: e.target.value, dateRange: 'Custom' }))}
            className={fieldCls}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Condense Mode</span>
          <select
            value={draft.condenseMode || 'None'}
            onChange={(e) => updateField('condenseMode', e.target.value)}
            className={`${fieldCls} bg-white`}
          >
            {CONDENSE_MODES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        {/* Read-only summary of which parameters drive the report. The
            selection itself is owned by the dashboard's LEFT sidebar (see
            `selectedParameters` plumbing in DashboardPage). Keeping the
            picker in two places used to confuse users — now there's a
            single source of truth. */}
        <div className="mt-2">
          <p className="text-xs font-semibold text-[#4D4D4D] mb-1">
            Parameters ({allParams.length} from sidebar)
          </p>
          <div className="max-h-32 overflow-y-auto border border-gray-200 rounded bg-gray-50">
            {allParams.length === 0 ? (
              <p className="p-3 text-xs text-gray-400 italic">
                Pick parameters in the Dashboard tab's left sidebar first.
              </p>
            ) : (
              <ul className="text-xs divide-y divide-gray-100">
                {allParams.map((p) => (
                  <li key={p.id} className="px-2 py-1.5">
                    <span className="text-[#4D4D4D]">{p.name}</span>
                    {p.unit && <span className="text-[#4D4D4D]"> ({p.unit})</span>}
                    <span className="block text-[10px] text-[#4D4D4D]">
                      {p.siteName} · {p.deviceName}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </SettingsSection>

      {/* ── View ──────────────────────────────────────────────────────── */}
      <SettingsSection
        title="View"
        keywords={['Header', 'Stat Table', 'Plot', 'Combine Plots', 'Data Table', 'Include Sections']}
        searchQuery={searchQuery}
      >
        <p className="text-xs font-semibold text-[#4D4D4D] mb-1">Include Sections</p>
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input type="checkbox"
            checked={draft.view?.header ?? true}
            onChange={(e) => updateNested('view', 'header', e.target.checked)} />
          Header
        </label>
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input type="checkbox"
            checked={draft.view?.statTable ?? true}
            onChange={(e) => updateNested('view', 'statTable', e.target.checked)} />
          Stat Table
        </label>
        <div className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <label className="flex items-center gap-2">
            <input type="checkbox"
              checked={draft.view?.plot ?? true}
              onChange={(e) => updateNested('view', 'plot', e.target.checked)} />
            Plot
          </label>
          <select
            value={draft.view?.plotMode || 'Combine Plots'}
            onChange={(e) => updateNested('view', 'plotMode', e.target.value)}
            disabled={draft.view?.plot === false}
            className={`flex-1 px-1.5 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:border-[#358ECD] disabled:opacity-50`}
          >
            <option>Combine Plots</option>
            <option>Separate Plots</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input type="checkbox"
            checked={draft.view?.dataTable ?? true}
            onChange={(e) => updateNested('view', 'dataTable', e.target.checked)} />
          Data Table
        </label>
      </SettingsSection>

      {/* ── Chart ──────────────────────────────────────────────────────────
          The new design lets the user define one OR MORE charts per
          report; each chart has its own title and a multi-select of up
          to 4 parameters. The global type/smooth/grid/legend toggles
          still live on `draft.chart` and apply to every chart. */}
      <SettingsSection
        title="Chart"
        keywords={['Chart Type', 'Smooth', 'Line', 'Area', 'Bar', 'Show Dots', 'Stack', 'Grid', 'Legend', 'Title', 'Add Parameters']}
        searchQuery={searchQuery}
      >
        {(() => {
          const MAX_PARAMS = 4
          const charts = Array.isArray(draft.charts) ? draft.charts : []
          const updateChart = (idx, patch) =>
            setDraft(d => ({
              ...d,
              charts: (d.charts || []).map((c, i) => i === idx ? { ...c, ...patch } : c),
            }))
          const removeChart = (idx) =>
            setDraft(d => ({ ...d, charts: (d.charts || []).filter((_, i) => i !== idx) }))
          const addChart = () =>
            setDraft(d => ({
              ...d,
              charts: [...(d.charts || []), { title: '', parameterIds: [] }],
            }))
          const toggleParamForChart = (idx, pid) => {
            const c = charts[idx] || { title: '', parameterIds: [] }
            const selected = (c.parameterIds || []).map(String)
            const next = new Set(selected)
            const k = String(pid)
            if (next.has(k)) next.delete(k)
            else if (next.size < MAX_PARAMS) next.add(k)
            updateChart(idx, { parameterIds: Array.from(next).map((v) => Number(v) || v) })
          }
          return (
            <>
              {charts.length === 0 && (
                <p className="text-xs text-[#4D4D4D] italic">
                  No charts yet — click "+ Add Chart" to define one.
                </p>
              )}
              {charts.map((c, idx) => {
                const selected = new Set((c.parameterIds || []).map(String))
                return (
                  <div key={idx} className={`pb-3 ${idx < charts.length - 1 ? 'border-b border-gray-100 mb-2' : ''}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-[#4D4D4D]">Chart {idx + 1}</p>
                      <button
                        type="button"
                        onClick={() => removeChart(idx)}
                        title={`Remove Chart ${idx + 1}`}
                        className="text-gray-400 hover:text-red-500 p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <label className="block mt-1">
                      <span className={labelCls}>Title</span>
                      <input
                        type="text"
                        value={c.title || ''}
                        onChange={(e) => updateChart(idx, { title: e.target.value })}
                        placeholder=""
                        className={fieldCls}
                      />
                    </label>
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-[#4D4D4D] mb-1">
                        Add Parameters <span className="font-normal text-gray-400">(Maximum {MAX_PARAMS})</span>
                      </p>
                      <div className="border border-gray-200 rounded max-h-44 overflow-y-auto bg-white">
                        {allParams.length === 0 ? (
                          <p className="p-2 text-[11px] text-gray-400 italic">
                            Pick parameters in the Dashboard tab's left sidebar first.
                          </p>
                        ) : (
                          <ul className="text-xs divide-y divide-gray-100">
                            {allParams.map((p) => {
                              const isSelected = selected.has(String(p.id))
                              const atCap = !isSelected && selected.size >= MAX_PARAMS
                              return (
                                <li key={p.id}>
                                  <label
                                    className={`flex items-start gap-2 px-2 py-1.5 ${atCap ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={atCap}
                                      onChange={() => toggleParamForChart(idx, p.id)}
                                      className="mt-0.5"
                                    />
                                    <span className="flex-1 truncate">
                                      {p.siteName ? `${p.siteName} | ` : ''}{p.deviceName ? `${p.deviceName} | ` : ''}{p.name}
                                      {p.unit && <span className="text-[#4D4D4D]"> ({p.unit})</span>}
                                    </span>
                                  </label>
                                </li>
                              )
                            })}
                          </ul>
                        )}
                      </div>
                      {selected.size >= MAX_PARAMS && (
                        <p className="text-[10px] text-amber-600 mt-1">Maximum {MAX_PARAMS} parameters per chart.</p>
                      )}
                    </div>
                  </div>
                )
              })}
              <button
                type="button"
                onClick={addChart}
                className="text-[#358ECD] text-xs hover:underline inline-flex items-center gap-0.5 mt-2"
              >
                <span className="text-base leading-none">+</span> Add Chart
              </button>

              {/* Global render options — apply to every chart entry. Kept
                  collapsed below the per-chart list so the per-chart UI
                  matches the XD as the primary surface. */}
              <details className="mt-3 group">
                <summary className="text-xs font-semibold text-[#4D4D4D] cursor-pointer select-none">
                  Render Options
                </summary>
                <div className="mt-2 space-y-2">
                  <label className="block">
                    <span className={labelCls}>Chart Type</span>
                    <select
                      value={draft.chart?.type || 'Line'}
                      onChange={(e) => updateNested('chart', 'type', e.target.value)}
                      className={`${fieldCls} bg-white`}
                    >
                      <option>Line</option>
                      <option>Area</option>
                      <option>Bar</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                    <input type="checkbox"
                      checked={draft.chart?.smooth ?? false}
                      onChange={(e) => updateNested('chart', 'smooth', e.target.checked)} />
                    Smooth lines
                  </label>
                  {/* Data-point markers only make sense for a single day's
                      worth of data; over a longer range they merge into a
                      cluttered band, so hide the option entirely past 24h. */}
                  {!reportSpanOverOneDay(draft) && (
                    <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                      <input type="checkbox"
                        checked={draft.chart?.showDots ?? false}
                        onChange={(e) => updateNested('chart', 'showDots', e.target.checked)} />
                      Show data points
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                    <input type="checkbox"
                      checked={draft.chart?.showGrid ?? true}
                      onChange={(e) => updateNested('chart', 'showGrid', e.target.checked)} />
                    Show grid lines
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                    <input type="checkbox"
                      checked={draft.chart?.showLegend ?? true}
                      onChange={(e) => updateNested('chart', 'showLegend', e.target.checked)} />
                    Show legend
                  </label>
                </div>
              </details>
            </>
          )
        })()}
      </SettingsSection>

      {/* ── Y-Axis ────────────────────────────────────────────────────── */}
      <SettingsSection
        title="Y-Axis"
        keywords={['Y Axis', 'Auto Scale', 'Min', 'Max', 'Unit', 'Log', 'Logarithmic']}
        searchQuery={searchQuery}
      >
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input type="checkbox"
            checked={draft.yAxis?.autoScale ?? true}
            onChange={(e) => updateNested('yAxis', 'autoScale', e.target.checked)} />
          Auto-scale
        </label>
        {/* Manual min/max — disabled while Auto-scale is on so the user
            sees the constraint visually instead of getting silent overrides. */}
        <label className="block">
          <span className={labelCls}>Min</span>
          <input
            type="number" step="any"
            value={draft.yAxis?.min ?? ''}
            onChange={(e) => updateNested('yAxis', 'min', e.target.value)}
            disabled={draft.yAxis?.autoScale ?? true}
            placeholder="auto"
            className={`${inputCls} disabled:opacity-50`}
          />
        </label>
        <label className="block">
          <span className={labelCls}>Max</span>
          <input
            type="number" step="any"
            value={draft.yAxis?.max ?? ''}
            onChange={(e) => updateNested('yAxis', 'max', e.target.value)}
            disabled={draft.yAxis?.autoScale ?? true}
            placeholder="auto"
            className={`${inputCls} disabled:opacity-50`}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input type="checkbox"
            checked={draft.yAxis?.showUnit ?? true}
            onChange={(e) => updateNested('yAxis', 'showUnit', e.target.checked)} />
          Show unit in label
        </label>
        <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
          <input type="checkbox"
            checked={draft.yAxis?.logarithmic ?? false}
            onChange={(e) => updateNested('yAxis', 'logarithmic', e.target.checked)} />
          Logarithmic scale
        </label>
      </SettingsSection>

      {/* ── Filter ────────────────────────────────────────────────────────
          Supports multiple per-parameter Min/Max filters. The legacy
          single-filter shape (`draft.filter`) is normalised into the
          `filters` array on first render so existing saved reports keep
          working — see the filter-normalisation effect in ReportView. */}
      <SettingsSection
        title="Filter"
        keywords={['Select Parameter', 'Min', 'Max', 'Filter Parameter', 'Add Filter']}
        searchQuery={searchQuery}
      >
        {(() => {
          const filters = Array.isArray(draft.filters) ? draft.filters : []
          const updateFilter = (idx, patch) =>
            setDraft(d => ({
              ...d,
              filters: (d.filters || []).map((f, i) => i === idx ? { ...f, ...patch } : f),
            }))
          const removeFilter = (idx) =>
            setDraft(d => ({ ...d, filters: (d.filters || []).filter((_, i) => i !== idx) }))
          const addFilter = () =>
            setDraft(d => ({
              ...d,
              filters: [...(d.filters || []), { parameterId: '', min: '', max: '' }],
            }))
          // Always show at least one row even when filters is empty —
          // matches the XD reference (Select Parameter visible from the
          // start, not gated behind a "+ Add Filter" click).
          const rows = filters.length === 0 ? [{ parameterId: '', min: '', max: '' }] : filters
          const usingPlaceholder = filters.length === 0
          return (
            <>
              {rows.map((f, idx) => (
                <div key={idx} className={`pb-3 ${idx < rows.length - 1 ? 'border-b border-gray-100 mb-2' : ''}`}>
                  <div className="flex items-start justify-between gap-1">
                    <label className="flex-1 block">
                      <span className={labelCls}>Select Parameter</span>
                      <select
                        value={f.parameterId || ''}
                        onChange={(e) => {
                          if (usingPlaceholder) {
                            // First interaction "lifts" the placeholder
                            // row into the persisted filters array.
                            setDraft(d => ({ ...d, filters: [{ ...f, parameterId: e.target.value }] }))
                          } else {
                            updateFilter(idx, { parameterId: e.target.value })
                          }
                        }}
                        className={`${fieldCls} bg-white`}
                      >
                        <option value="">— pick a parameter —</option>
                        {allParams.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.siteName ? `${p.siteName} | ` : ''}{p.deviceName ? `${p.deviceName} | ` : ''}{p.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {/* Remove row — only shown when there's more than one
                        real filter so the user can't accidentally clear
                        every filter at once. */}
                    {!usingPlaceholder && filters.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFilter(idx)}
                        title="Remove this filter"
                        className="mt-5 p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-[#4D4D4D] mt-2">Filter Parameter</p>
                  <label className="block">
                    <span className="text-[11px] text-[#4D4D4D]">Min</span>
                    <input
                      type="number" step="any"
                      value={f.min ?? ''}
                      onChange={(e) => {
                        if (usingPlaceholder) {
                          setDraft(d => ({ ...d, filters: [{ ...f, min: e.target.value }] }))
                        } else {
                          updateFilter(idx, { min: e.target.value })
                        }
                      }}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-[#4D4D4D]">Max</span>
                    <input
                      type="number" step="any"
                      value={f.max ?? ''}
                      onChange={(e) => {
                        if (usingPlaceholder) {
                          setDraft(d => ({ ...d, filters: [{ ...f, max: e.target.value }] }))
                        } else {
                          updateFilter(idx, { max: e.target.value })
                        }
                      }}
                      className={inputCls}
                    />
                  </label>
                </div>
              ))}
              <button
                type="button"
                onClick={addFilter}
                className="text-[#358ECD] text-xs hover:underline inline-flex items-center gap-0.5 mt-1"
              >
                <span className="text-base leading-none">+</span> Add Filter
              </button>
            </>
          )
        })()}
      </SettingsSection>

      {/* ── Header / Footer ───────────────────────────────────────────── */}
      <SettingsSection
        title="Header/Footer"
        keywords={['Top Title', 'Report Title', 'Subtitle', 'Date Prepared', 'Time Prepared', 'Footer', 'Page Number', 'Extra Footer']}
        searchQuery={searchQuery}
      >
        <p className="text-xs font-bold text-[#4D4D4D]">Header</p>
        {([
          ['topTitle',     'Top Title'],
          ['reportTitle',  'Report Title'],
          ['subtitle',     'Subtitle'],
          ['datePrepared', 'Date Prepared'],
        ]).map(([key, label]) => {
          const row = draft.headerFooter?.[key] || { enabled: true, value: '' }
          return (
            <div key={key} className="space-y-1">
              <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => updateHF(key, { enabled: e.target.checked })}
                />
                {label}
              </label>
              <input
                type="text"
                value={row.value || ''}
                onChange={(e) => updateHF(key, { value: e.target.value })}
                disabled={!row.enabled}
                placeholder={label}
                className={`${inputCls} disabled:opacity-50`}
              />
            </div>
          )
        })}
        {(() => {
          const row = draft.headerFooter?.timePrepared || { enabled: true, value: '12-hour' }
          return (
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => updateHF('timePrepared', { enabled: e.target.checked })}
                />
                Time Prepared
              </label>
              <select
                value={row.value || '12-hour'}
                onChange={(e) => updateHF('timePrepared', { value: e.target.value })}
                disabled={!row.enabled}
                className={`${inputCls} bg-white disabled:opacity-50`}
              >
                <option value="12-hour">12-hour</option>
                <option value="24-hour">24-hour</option>
              </select>
            </div>
          )
        })()}
        <p className="text-xs font-bold text-[#4D4D4D] mt-2">Footer</p>
        {/* Page Number row — checkbox + text. Default "Page 1 of 2" is the
            template the design ships; the user can swap in any token-style
            string (the PDF renderer will substitute the current page later). */}
        {(() => {
          const row = draft.headerFooter?.pageNumber || { enabled: true, value: 'Page 1 of 2' }
          return (
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => updateHF('pageNumber', { enabled: e.target.checked })}
                />
                Page Number
              </label>
              <input
                type="text"
                value={row.value || ''}
                onChange={(e) => updateHF('pageNumber', { value: e.target.value })}
                disabled={!row.enabled}
                placeholder="Page 1 of 2"
                className={`${inputCls} disabled:opacity-50`}
              />
            </div>
          )
        })()}
        {/* Extra Footer row — second optional free-form line beneath the
            page number. */}
        {(() => {
          const row = draft.headerFooter?.extraFooter || { enabled: false, value: '' }
          return (
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-xs text-[#4D4D4D]">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => updateHF('extraFooter', { enabled: e.target.checked })}
                />
                Extra Footer
              </label>
              <input
                type="text"
                value={row.value || ''}
                onChange={(e) => updateHF('extraFooter', { value: e.target.value })}
                disabled={!row.enabled}
                placeholder="Extra Footer"
                className={`${inputCls} disabled:opacity-50`}
              />
            </div>
          )
        })()}
      </SettingsSection>

      {/* ── PDF Format ────────────────────────────────────────────────── */}
      <SettingsSection
        title="PDF Format"
        keywords={['Size', 'Orientation', 'Letter', 'Legal', 'A4', 'Tabloid', 'Portrait', 'Landscape', 'Include Sections']}
        searchQuery={searchQuery}
      >
        {/* "Include Sections" sub-heading kept per the design — it scopes
            the controls under it as "what goes into the rendered PDF". */}
        <p className="text-xs font-bold text-[#4D4D4D]">Include Sections</p>
        <label className="block">
          <span className={labelCls}>Size</span>
          <select
            value={draft.pdfFormat?.paperSize || 'Letter (8.5 x 11 in)'}
            onChange={(e) => updateNested('pdfFormat', 'paperSize', e.target.value)}
            className={`${fieldCls} bg-white`}>
            <option>Letter (8.5 x 11 in)</option>
            <option>Legal (8.5 x 14 in)</option>
            <option>A4 (210 × 297 mm)</option>
            <option>Tabloid (11 x 17 in)</option>
          </select>
        </label>
        <label className="block">
          <span className={labelCls}>Orientation</span>
          <select
            value={draft.pdfFormat?.orientation || 'Portrait'}
            onChange={(e) => updateNested('pdfFormat', 'orientation', e.target.value)}
            className={`${fieldCls} bg-white`}>
            <option>Portrait</option>
            <option>Landscape</option>
          </select>
        </label>
      </SettingsSection>
    </aside>
  )
}

// ─── Report content (header + stats + chart + table + footer) ────────────────
function ReportContent({ settings, data, loading, customerTz }) {
  // Short name of the zone the chart/table are drawn in ("EDT", "IST", …).
  // A report renders in ITS OWN Timezone setting, which can differ from the
  // signed-in account's — the header's "As of … EDT" then disagrees with the
  // axis and there was no way to tell which zone the plot used. The Data Table
  // already carries a "Time <zone>" header; this puts the same fact on the chart.
  const chartTzLabel = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz, timeZoneName: 'short',
      }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || ''
    } catch { return '' }
  })()
  // The requested window as real instants, resolved in the report's timezone
  // (the From/To fields hold wall-clock strings for that zone).
  const rangeMs = (() => {
    const parse = (s) => (s ? parseWallClockInTzToMs(String(s).replace('T', ' '), customerTz) : null)
    let from = parse(settings?.dateFrom)
    let to   = parse(settings?.dateTo)
    // A saved preset report may not have persisted From/To (or stored them in a
    // form we can't parse) — resolve the preset so the year detection and month
    // axis still fire for a saved "This Year" / "Last Year".
    if ((from == null || to == null) && settings?.dateRange && settings.dateRange !== 'Custom') {
      const pr = rangeFromPreset(settings.dateRange, customerTz)
      if (pr) { from = from ?? parse(pr.from); to = to ?? parse(pr.to) }
    }
    return (from != null && to != null && to > from) ? { from, to } : null
  })()
  // "Any of the year options": the two year presets (case-insensitive), plus a
  // custom range long enough to be a year (so "go back a year in custom"
  // behaves the same).
  const DAY_MS = 86_400_000
  const YEARISH_MS = 300 * DAY_MS
  const drLower = String(settings?.dateRange || '').trim().toLowerCase()
  const isYearRange = !!rangeMs && (
    drLower === 'this year' || drLower === 'last year' ||
    (rangeMs.to - rangeMs.from) >= YEARISH_MS
  )
  // A week-ish window (2–8 days) gets explicit half-day ticks: the date at
  // midnight, "12:00" at noon. Auto ticks land every ~12h at this width, and
  // with date-only labels that repeats each date twice ("Aug 8  Aug 8  Aug 9
  // …"). Beyond ~8 days the automatic ticks spread to a day or more apart, so
  // date-only labels stop repeating and two labels per day would just crowd.
  const spanRangeMs = rangeMs ? rangeMs.to - rangeMs.from : 0
  const isWeekRange = !!rangeMs && !isYearRange &&
    spanRangeMs >= 2 * DAY_MS && spanRangeMs <= 8 * DAY_MS
  // Anything between a week and a year gets explicit day ticks, thinned to a
  // readable count. Left to itself the chart spaces ticks by pixels, not days,
  // so date-only labels repeat ("Aug 1  Aug 1 … Aug 8  Aug 8") and skip days.
  const isMultiDayRange = !!rangeMs && !isYearRange && !isWeekRange &&
    spanRangeMs > 8 * DAY_MS
  // A single-day window (Today / Yesterday / any range under ~2 days) gets
  // explicit even 2-hour ticks — the same axis the dashboard graphs' Day view
  // draws. Without this it fell to auto ticks, which don't match the graphs.
  const isSingleDayRange = !!rangeMs && !isYearRange && !isWeekRange && !isMultiDayRange &&
    spanRangeMs > 0 && spanRangeMs <= 2 * DAY_MS

  const params = settings?.parameters || []
  const view   = settings?.view || {}
  const hf     = settings?.headerFooter || {}
  // Support both the old (showStats / showChart / showTable) and the
  // current (statTable / plot / dataTable) view keys so a saved report
  // from before the schema update still renders cleanly.
  const showHeader = view.header    ?? true
  const showStats  = view.statTable ?? view.showStats ?? true
  const showChart  = view.plot      ?? view.showChart ?? true
  const showTable  = view.dataTable ?? view.showTable ?? false
  const separatePlots = (view.plotMode || 'Combine Plots') === 'Separate Plots'

  // ── Resolve header/footer pieces ─────────────────────────────────────
  const get = (k) => hf?.[k] || { enabled: true, value: '' }
  const todayStr = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz, year: 'numeric', month: 'long', day: 'numeric',
      }).format(new Date())
    } catch { return new Date().toDateString() }
  })()
  const timeStr = (() => {
    const is12 = (get('timePrepared').value || '12-hour') === '12-hour'
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: is12,
      }).format(new Date())
    } catch { return new Date().toLocaleTimeString() }
  })()
  const datePreparedTxt = get('datePrepared').enabled ? (get('datePrepared').value || todayStr) : ''
  const timePreparedTxt = get('timePrepared').enabled ? timeStr : ''

  // Apply Settings → Data → Condense Mode and then → Filter Min/Max in a
  // single pass. Condense bucket-averages the raw points; Filter drops
  // values outside each per-parameter [min, max] range.
  //
  // `settings.filters` is the canonical array shape (1-or-more entries);
  // older saved reports stored a single `settings.filter` object — we
  // normalise both to the same array here so the rest of the function
  // only deals with one shape. A filter with no parameterId applies to
  // every parameter (legacy "universal" behaviour); a filter with an id
  // applies only to that parameter.
  const showErrorValue = settings?.showErrorValue === 'Yes'
  const condenseMode = settings?.condenseMode || 'None'
  const filtersRaw = Array.isArray(settings?.filters) && settings.filters.length
    ? settings.filters
    : (settings?.filter && (settings.filter.parameterId || settings.filter.min || settings.filter.max)
        ? [settings.filter]
        : [])
  const filtersKey = JSON.stringify(filtersRaw)

  const effectiveData = useMemo(
    () => computeEffectiveData(data, settings),
    // Derived keys (not `settings` identity) so we only recompute when the
    // inputs that actually change the result do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, condenseMode, filtersKey, showErrorValue],
  )

  if (!params.length) {
    return (
      <div className="h-full min-h-[260px] flex items-center justify-center text-sm text-gray-400">
        Select Parameter(s) in EDIT to Start Report
      </div>
    )
  }
  if (loading) {
    return (
      <div className="h-full min-h-[260px] flex items-center justify-center text-sm text-[#4D4D4D] gap-2">
        <Loader className="w-4 h-4 animate-spin" /> Loading report data…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {showHeader && (
        <header className="border-b border-gray-200 pb-3 space-y-1">
          {get('topTitle').enabled && get('topTitle').value && (
            <p className="text-xs uppercase tracking-wider text-[#4D4D4D]">{get('topTitle').value}</p>
          )}
          {get('reportTitle').enabled && (
            <h1 className="text-xl font-bold text-[#4D4D4D]">
              {get('reportTitle').value || settings?.name || 'Report'}
              {settings?.includeTimestamp && (
                <span className="ml-2 text-sm font-normal text-[#4D4D4D]">
                  — {todayStr}{timeStr ? `, ${timeStr}` : ''}
                </span>
              )}
            </h1>
          )}
          {get('subtitle').enabled && get('subtitle').value && (
            <p className="text-sm text-gray-600">{get('subtitle').value}</p>
          )}
          {(datePreparedTxt || timePreparedTxt) && (
            <p className="text-xs text-[#4D4D4D]">
              {datePreparedTxt && <>Date prepared: <span className="text-[#4D4D4D]">{datePreparedTxt}</span></>}
              {datePreparedTxt && timePreparedTxt && <span className="mx-2">·</span>}
              {timePreparedTxt && <>Time prepared: <span className="text-[#4D4D4D]">{timePreparedTxt}</span></>}
            </p>
          )}
          {settings?.description && (
            <p className="text-sm text-[#4D4D4D] mt-2 whitespace-pre-wrap">{settings.description}</p>
          )}
        </header>
      )}
      {showStats && (
        <div>
          <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3 text-center">Stats Table</h3>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Site</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Device</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Sensor</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Parameter</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Samples</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Min</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Max</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Mean</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3 border-r border-gray-200">Median</th>
                  <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] text-center px-3 py-3">Std</th>
                </tr>
              </thead>
              <tbody>
                {params.map((p) => {
                  const stats = effectiveData[p.id]?.stats || {}
                  const fmt = (n) => (n == null || !Number.isFinite(n)) ? '—' : n.toFixed(2)
                  return (
                    <tr key={p.id} className="border-t border-gray-200">
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{p.siteName || '—'}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{p.deviceName || '—'}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{p.sensorName || '—'}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{p.name}{p.unit ? ` (${p.unit})` : ''}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{stats.count ?? 0}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{fmt(stats.min)}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{fmt(stats.max)}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{fmt(stats.mean)}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center border-r border-gray-200">{fmt(stats.median)}</td>
                      <td className="common-size15 text-[#4D4D4D] px-3 py-3 text-center">{fmt(stats.std)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showChart && (() => {
        // Render helper for a single combined chart from a list of
        // parameters. Used both for the new per-chart array and the
        // legacy fallback. `title` becomes the chart's heading; pass
        // null to omit the heading (Separate Plots case uses the
        // parameter name as the per-plot label).
        const chartOpts   = settings?.chart || {}
        const chartType   = chartOpts.type      || 'Line'
        // Default OFF: charts show the measured points joined straight, and
        // smoothing is something the user opts into in Chart settings.
        const smoothLines = chartOpts.smooth    ?? false
        // Suppress dots past a one-day span even if a report saved earlier
        // (with a shorter range) still has showDots=true.
        const showDots    = (chartOpts.showDots ?? false) && !reportSpanOverOneDay(settings)
        const showGrid    = chartOpts.showGrid   ?? true
        const showLegend  = chartOpts.showLegend ?? true
        const curveType   = smoothLines ? 'monotone' : 'linear'

        const yAxisOpts   = settings?.yAxis || {}
        const autoScale   = yAxisOpts.autoScale   ?? true
        const yMin        = yAxisOpts.min !== '' && yAxisOpts.min != null ? Number(yAxisOpts.min) : undefined
        const yMax        = yAxisOpts.max !== '' && yAxisOpts.max != null ? Number(yAxisOpts.max) : undefined
        const logarithmic = yAxisOpts.logarithmic ?? false
        const showUnit    = yAxisOpts.showUnit    ?? true
        const yDomain     = autoScale ? undefined : [
            yMin != null ? yMin : 'auto',
            yMax != null ? yMax : 'auto',
        ]

        const renderChart = (chartParams, title, keySuffix) => {
            const units = [...new Set(chartParams.map(p => p.unit).filter(Boolean))]
            // Y-axis title: only when every selected param shares the same single unit
            const yAxisUnitLabel = showUnit && units.length === 1 ? units[0] : ''
            // keepNulls + chartPoints → the line breaks at hidden-status gaps.
            const rows = mergePoints(effectiveData, chartParams, true, 'chartPoints')
            // Tick format follows the range the report actually covers. It used
            // to be hardcoded to HH:mm, so a Last Month / year report printed a
            // clock time on every tick with no date anywhere — and because
            // consecutive ticks fall on different days, the times jumped around
            // and looked like the axis ran backwards.
            const spanMs = rows.length > 1 ? rows[rows.length - 1].x - rows[0].x : 0
            const formatXTick = makeTimeAxisFormatter(spanMs, customerTz)

            // Year ranges get one hash mark per month across the WHOLE selected
            // year, not tick positions picked from wherever the data happens to
            // start and stop. The axis is pinned to the requested window so the
            // twelve months are all present even when the device only reported
            // for part of the year (which is itself worth seeing).
            const monthTicks = isYearRange ? monthStartsInRange(rangeMs.from, rangeMs.to, customerTz) : []
            const monthLabels = monthTicks.length ? monthTickLabels(monthTicks, customerTz) : null
            const useMonthAxis = monthTicks.length > 1

            // Week-ish window: date at midnight, "12:00" at noon.
            const halfDayTicks = isWeekRange ? halfDayTicksInRange(rangeMs.from, rangeMs.to, customerTz) : []
            const halfDayLabels = halfDayTicks.length ? halfDayTickLabels(halfDayTicks, customerTz) : null
            const useHalfDayAxis = !useMonthAxis && halfDayTicks.length > 1

            // Multi-week window: one label per day (or every Nth day), all distinct.
            const dayTicks = isMultiDayRange ? dailyTicksInRange(rangeMs.from, rangeMs.to, customerTz) : []
            const dayLabels = dayTicks.length
              ? dayMonthTickLabels(dayTicks, customerTz, { order: 'month-day' })
              : null
            const useDayAxis = !useMonthAxis && !useHalfDayAxis && dayTicks.length > 1

            // Single-day window: even 2-hour ticks (date at midnight, HH:MM else),
            // matching the dashboard graphs' Day view.
            const twoHourTicks = isSingleDayRange ? twoHourTicksInRange(rangeMs.from, rangeMs.to, customerTz) : []
            const twoHourLabels = twoHourTicks.length ? twoHourTickLabels(twoHourTicks, customerTz) : null
            const useTwoHourAxis = !useMonthAxis && !useHalfDayAxis && !useDayAxis && twoHourTicks.length > 1

            const explicitTicks = useMonthAxis ? monthTicks
              : useHalfDayAxis ? halfDayTicks
              : useDayAxis ? dayTicks
              : useTwoHourAxis ? twoHourTicks
              : null
            const explicitLabels = useMonthAxis ? monthLabels
              : useHalfDayAxis ? halfDayLabels
              : useDayAxis ? dayLabels
              : twoHourLabels
            const usePinnedAxis = useMonthAxis || useHalfDayAxis || useDayAxis || useTwoHourAxis
            // Right edge of a pinned axis. A rolling preset's `to` is END OF
            // TODAY (23:59), so after the device's last reading the axis kept
            // running to day's end — the trailing gap the user flagged on Last
            // 30 Days. Year keeps the FULL window (all months, even a partly-
            // reported year — see the month-axis request); shorter ranges end
            // at the last data point so there's no empty tail.
            const dataMaxX = rows.length ? rows[rows.length - 1].x : null
            const pinnedRight = (usePinnedAxis && rangeMs)
              ? (useMonthAxis
                  ? rangeMs.to
                  : Math.min(rangeMs.to, Math.max(rangeMs.from, dataMaxX ?? rangeMs.to)))
              : null
            // Drop ticks that would fall past the (possibly shortened) right edge.
            const pinnedTicks = (explicitTicks && pinnedRight != null)
              ? explicitTicks.filter((t) => t >= rangeMs.from && t <= pinnedRight)
              : explicitTicks
            return (
            <ResponsiveContainer key={`${keySuffix}-${chartType}-${curveType}-${autoScale}-${yMin}-${yMax}-${logarithmic}-${showUnit}`} width="100%" height={Math.min(360, 220 + chartParams.length * 20)}>
                <ComposedChart data={rows} margin={{ top: 10, right: 20, left: yAxisUnitLabel ? 10 : 0, bottom: showLegend ? 10 : 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" strokeOpacity={showGrid ? 1 : 0} />
                    <XAxis
                        dataKey="x" type="number" scale="time"
                        domain={usePinnedAxis ? [rangeMs.from, pinnedRight] : ['dataMin', 'dataMax']}
                        {...(usePinnedAxis
                            // interval=0 forces every mark to draw rather than
                            // letting Recharts thin them out.
                            ? { ticks: pinnedTicks, interval: 0, tickFormatter: (v) => explicitLabels.get(v) ?? '' }
                            : { tickFormatter: formatXTick, minTickGap: 40 })}
                        tick={{ fontSize: 11, fill: '#888' }}
                        allowDataOverflow={usePinnedAxis}
                    />
                    <YAxis
                        tick={{ fontSize: 11, fill: '#888' }}
                        width={yAxisUnitLabel ? 55 : 45}
                        scale={logarithmic ? 'log' : 'auto'}
                        domain={yDomain}
                        allowDataOverflow={!autoScale}
                        label={yAxisUnitLabel ? {
                            value: yAxisUnitLabel, angle: -90, position: 'insideLeft',
                            offset: 10, style: { fontSize: 10, fill: '#888', textAnchor: 'middle' },
                        } : undefined}
                    />
                    <Tooltip
                        isAnimationActive={false}
                        wrapperStyle={{ transition: 'none' }}
                        // Shared XD hover box (see ChartHoverTooltip). Same zone +
                        // format as the axis beside it (formatMediaTimestamp), so
                        // the two never disagree.
                        content={(tp) => (
                            <ChartHoverTooltip
                                {...tp}
                                formatLabel={(v) => formatMediaTimestamp(v, customerTz)}
                                describe={(entry) => {
                                    const pid = String(entry?.dataKey || '').replace('p_', '')
                                    const p = chartParams.find(pp => String(pp.id) === pid)
                                    const v = entry?.value
                                    const vv = typeof v?.toFixed === 'function' ? v.toFixed(2) : v
                                    return {
                                        name: p?.name || pid,
                                        valueText: `${vv}${p?.unit ? ` ${p.unit}` : ''}`,
                                        color: entry?.color || entry?.stroke || entry?.fill,
                                    }
                                }}
                            />
                        )}
                    />
                    {showLegend && (
                        <Legend
                            formatter={(value) => {
                                const pid = value.replace('p_', '')
                                const p = chartParams.find(pp => String(pp.id) === pid)
                                const name = p?.name || value
                                return showUnit && p?.unit ? `${name} (${p.unit})` : name
                            }}
                            wrapperStyle={{ fontSize: 11 }}
                        />
                    )}
                    {chartParams.map((p, idx) => {
                        const color = CHART_COLORS[idx % CHART_COLORS.length]
                        const commonProps = {
                            key: p.id,
                            dataKey: `p_${p.id}`,
                            isAnimationActive: false,
                        }
                        if (chartType === 'Bar') {
                            return <Bar {...commonProps} fill={color} barSize={6} />
                        }
                        if (chartType === 'Area') {
                            return (
                                <Area
                                    {...commonProps}
                                    type={curveType}
                                    stroke={color}
                                    fill={color}
                                    fillOpacity={0.2}
                                    strokeWidth={2}
                                    dot={showDots ? { r: 3 } : false}
                                    connectNulls={false}
                                />
                            )
                        }
                        // Default: Line
                        return (
                            <Line
                                {...commonProps}
                                type={curveType}
                                stroke={color}
                                strokeWidth={2}
                                dot={showDots ? { r: 3 } : false}
                                connectNulls={false}
                            />
                        )
                    })}
                </ComposedChart>
            </ResponsiveContainer>
            )
        }

        // Resolve a parameter id (number or string) to its full param
        // record (with name/unit/site/device/sensor). Drops ids that
        // don't appear in the report's selected parameters.
        const resolveParam = (pid) => params.find((p) => String(p.id) === String(pid))

        // Settings → Chart → charts[]: drives the new per-chart layout.
        // Each entry renders its own titled chart with only the
        // parameters it lists.
        const userCharts = Array.isArray(settings?.charts) ? settings.charts : []
        const definedCharts = userCharts
            .map((c, i) => {
                const cp = (c.parameterIds || []).map(resolveParam).filter(Boolean)
                return cp.length ? { title: c.title || `Chart ${i + 1}`, params: cp, key: `uc-${i}` } : null
            })
            .filter(Boolean)

        if (definedCharts.length > 0) {
            return (
                <div className="select-none space-y-6">
                    {definedCharts.map(({ title, params: cp, key }) => (
                        <div key={key}>
                            {title && (
                                <h3 className="text-sm font-bold text-[#4D4D4D] mb-2 text-center">{title}</h3>
                            )}
                            {renderChart(cp, title, key)}
                            <div className="text-center text-xs text-gray-600 mt-2">
                                {cp.map((p) => p.name).join(', ')}
                                {chartTzLabel ? ` · times in ${chartTzLabel}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )
        }

        // Legacy fallback — no charts[] defined. Show the legacy combined
        // chart (or one chart per parameter when Separate Plots is on),
        // pulling from `params` (everything selected on the report).
        return (
            <div className="select-none">
                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3 text-center">Chart</h3>
                {separatePlots ? (
                    <div className="space-y-4">
                        {params.map((p, idx) => (
                            <div key={`sp-${p.id}`}>
                                <p className="text-xs font-semibold text-[#4D4D4D] text-center mb-1">
                                    {p.name}{p.unit ? ` (${p.unit})` : ''}
                                </p>
                                {renderChart([p], null, `sp-${p.id}-${idx}`)}
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {renderChart(params, null, 'combined')}
                        <div className="text-center text-xs text-gray-600 mt-2">
                            {params.map((p) => p.name).join(', ')}
                            {chartTzLabel ? ` · times in ${chartTzLabel}` : ''}
                        </div>
                    </>
                )}
            </div>
        )
      })()}

      {/* ── Data Table — grouped by device so a device's selected
           parameters all share one table (timestamp + one value column per
           parameter). The header rows (Site / Device / Sensor) come from
           the device itself; the column header lists each parameter and
           its unit. Sensors of the same device but different probes are
           still grouped together since they share a device. ── */}
      {showTable && (
        <div className="space-y-6">
          <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3 text-center">Data Table</h3>
          {(() => {
            // Group parameters by deviceName. We key on deviceName because
            // the API doesn't surface a stable device_id here; same-device
            // parameters share name, site, and sensor identity from the
            // sidebar payload.
            const groups = new Map()
            for (const p of params) {
              const key = p.deviceName || `__nodev_${p.id}`
              if (!groups.has(key)) groups.set(key, [])
              groups.get(key).push(p)
            }

            const fmtTs = (x) => {
              const d = new Date(x)
              if (isNaN(d.getTime())) return String(x)
              try {
                return new Intl.DateTimeFormat('en-CA', {
                  timeZone: customerTz,
                  year: 'numeric', month: '2-digit', day: '2-digit',
                  hour: '2-digit', minute: '2-digit', second: '2-digit',
                  hour12: false,
                }).format(d).replace(',', '')
              } catch { return d.toLocaleString() }
            }

            return Array.from(groups.entries()).map(([deviceKey, devParams]) => {
              // Build a unified timestamp axis from every parameter in this
              // device group, then look up each parameter's value at that
              // timestamp. Newest first.
              const tsMap = new Map() // x -> { [paramId]: point }
              for (const p of devParams) {
                for (const pt of (effectiveData[p.id]?.points || [])) {
                  if (pt.x == null) continue
                  if (pt.y == null && pt.err == null) continue
                  const x = typeof pt.x === 'string' ? new Date(pt.x).getTime() : Number(pt.x)
                  if (!tsMap.has(x)) tsMap.set(x, {})
                  tsMap.get(x)[p.id] = pt
                }
              }
              const rows = Array.from(tsMap.entries())
                .sort((a, b) => b[0] - a[0])
                .map(([x, byId]) => ({ x, byId }))

              const headProto = devParams[0] || {}
              return (
                <div key={`dt-${deviceKey}`} className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3 border-r border-gray-200 w-1/4">Site</td>
                        <td className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3" colSpan={devParams.length}>{headProto.siteName || '—'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3 border-r border-gray-200">Device</td>
                        <td className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3" colSpan={devParams.length}>{headProto.deviceName || '—'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3 border-r border-gray-200">Sensor</td>
                        <td className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3" colSpan={devParams.length}>
                          {Array.from(new Set(devParams.map(p => p.sensorName).filter(Boolean))).join(', ') || '—'}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="common-size15 text-[#4D4D4D] bg-[#F1F2F4] font-bold text-left px-3 py-3 border-r border-gray-200">Timestamp</th>
                        {devParams.map((p) => (
                          <th key={p.id} className="common-size15 text-[#4D4D4D] font-bold bg-[#F1F2F4] text-left px-3 py-3 border-r last:border-r-0 border-gray-200">
                            {p.name}{p.unit ? ` (${p.unit})` : ''}
                          </th>
                        ))}
                      </tr>
                      {rows.length === 0 ? (
                        <tr>
                          <td className="common-size15 text-[#4D4D4D] text-left px-3 py-3 italic" colSpan={1 + devParams.length}>
                            No data in range.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                            <td className="common-size15 text-[#4D4D4D] text-left px-3 py-3 border-r border-gray-200">{fmtTs(row.x)}</td>
                            {devParams.map((p) => {
                              const v = tableCellValue(row.byId[p.id])
                              return (
                                <td key={p.id} className="common-size15 text-[#4D4D4D] text-left px-3 py-3 border-r last:border-r-0 border-gray-200">
                                  {typeof v === 'number' ? v.toFixed(2) : (v ?? '—')}
                                </td>
                              )
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )
            })
          })()}
        </div>
      )}

      {/* ── Footer: page number row + an optional extra-footer free-text row.
           Page number is purely text on screen (PDF print pagination is the
           browser's job via @page CSS); the user can swap in any template
           string like "Page 1 of 2" — the field is free-form. */}
      {(get('pageNumber').enabled || get('extraFooter').enabled) && (
        <footer className="border-t border-gray-200 pt-3 text-xs text-[#4D4D4D] text-center space-y-0.5">
          {get('pageNumber').enabled && (
            <div>{get('pageNumber').value || 'Page 1 of 1'}</div>
          )}
          {get('extraFooter').enabled && get('extraFooter').value && (
            <div>{get('extraFooter').value}</div>
          )}
        </footer>
      )}
    </div>
  )
}

// ─── Report List Modal ───────────────────────────────────────────────────────
// "View All Reports" opens this overlay — header "Report List", a column
// header row ("Report Name"), then one row per saved report. Click the name
// to open that report; click "Remove" to soft-delete it. CLOSE button at
// the bottom closes the modal without changing the report currently visible
// on the Report tab underneath.
export function ReportListModal({ open, projectId, onClose, onOpenReport, onReportListChange }) {
  const [reports,   setReports]   = useState([])
  const [schedules, setSchedules] = useState([])
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState(null)
  const [busy,      setBusy]      = useState({})  // { [key]: 'toggle' | 'remove' }
  // Format Next-Run timestamps in the user's profile timezone (not the
  // browser's — a user checking from a different device shouldn't see
  // their schedule shift by hours).
  const rawTz      = useAuthStore(s => s.user?.timezone)
  const customerTz = resolveIanaTz(rawTz)

  useEffect(() => {
    if (!open || !projectId) return
    let cancelled = false
    setLoading(true); setError(null)
    // Reports + schedules load in parallel so the merge below always
    // has both sets by the time we render rows.
    Promise.all([
      listReports(projectId).catch(() => []),
      listSchedules(projectId).catch(() => []),
    ])
      .then(([rs, ss]) => {
        if (cancelled) return
        setReports(Array.isArray(rs) ? rs : (rs?.reports || []))
        setSchedules(Array.isArray(ss) ? ss : [])
      })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load reports') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, projectId])

  // Close on Escape — matches the rest of the app's modal UX.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Group ALL schedules by report_id — reports can carry a schedule per
  // transport (Email / FTP / GLOS / NDBC), and each one gets its own
  // Disable + Remove row nested under the report name.
  const schedulesByReport = new Map()
  for (const s of schedules) {
    const arr = schedulesByReport.get(s.report_id) || []
    arr.push(s)
    schedulesByReport.set(s.report_id, arr)
  }
  // Stable order — display transports in this order so the list reads
  // the same on every refresh regardless of when each schedule was
  // created.
  const TRANSPORT_ORDER = { email: 0, ftp: 1, ndbc: 2, glos: 3 }
  const TRANSPORT_LABEL = { email: 'Email', ftp: 'SFTP/FTP', ndbc: 'NDBC', glos: 'GLOS' }
  for (const arr of schedulesByReport.values()) {
    arr.sort((a, b) => (TRANSPORT_ORDER[a.transport] ?? 99) - (TRANSPORT_ORDER[b.transport] ?? 99))
  }

  const fmtNextRun = (s) => {
    if (!s?.next_run_at) return ''
    // Server stores `next_run_at` as UTC "YYYY-MM-DD HH:MM:SS". Parse as
    // UTC, then format in the *user's profile timezone* so the label
    // matches their working hours regardless of the browser's TZ.
    const d = new Date(s.next_run_at.replace(' ', 'T') + 'Z')
    if (isNaN(d.getTime())) return s.next_run_at
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: customerTz,
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short',
      }).format(d)
    } catch { return d.toString() }
  }

  const handleToggle = async (schedule) => {
    if (!schedule) return
    const k = `sched-${schedule.id}`
    setBusy(b => ({ ...b, [k]: 'toggle' }))
    try {
      const updated = await updateSchedule(schedule.id, { enabled: !schedule.enabled })
      setSchedules(ss => ss.map(s => s.id === schedule.id ? { ...s, ...updated } : s))
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Could not update schedule')
    } finally {
      setBusy(b => { const n = { ...b }; delete n[k]; return n })
    }
  }

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this report? This deletes the report and any schedules attached to it.')) return
    const k = `rep-${id}`
    setBusy(b => ({ ...b, [k]: 'remove' }))
    try {
      await deleteReport(id)
      setReports(rs => rs.filter(r => r.id !== id))
      // Schedules attached to the removed report cascade on the server.
      setSchedules(ss => ss.filter(s => s.report_id !== id))
      onReportListChange?.()
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Failed to remove report')
    } finally {
      setBusy(b => { const n = { ...b }; delete n[k]; return n })
    }
  }

  // Remove just one schedule (transport) — leaves the report + any other
  // schedules on the report intact.
  const handleRemoveSchedule = async (schedule) => {
    if (!schedule) return
    const label = TRANSPORT_LABEL[schedule.transport] || schedule.transport
    if (!window.confirm(`Remove the ${label} schedule?`)) return
    const k = `sched-rm-${schedule.id}`
    setBusy(b => ({ ...b, [k]: 'remove' }))
    try {
      await deleteSchedule(schedule.id)
      setSchedules(ss => ss.filter(s => s.id !== schedule.id))
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Could not remove schedule')
    } finally {
      setBusy(b => { const n = { ...b }; delete n[k]; return n })
    }
  }

  if (!open) return null

  return (
    <div
      // Darker backdrop (60% black) so text content from widgets under
      // the modal — e.g. Field Notes with sample body copy — doesn't
      // bleed through the overlay and clash with the report list.
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <div className="px-8 py-6 border-b border-gray-100">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Report List</h2>
        </div>

        {/* Body */}
        <div className="px-8 py-6 flex-1 min-h-0">
          <p className="common-size18 font-bold text-[#4D4D4D] mb-3">Report Name</p>

          {loading && (
            <div className="py-6 text-center text-sm text-[#4D4D4D] inline-flex items-center justify-center gap-2 w-full">
              <Loader className="w-4 h-4 animate-spin" /> Loading reports…
            </div>
          )}
          {error && (
            <div className="py-3 px-3 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {!loading && !error && reports.length === 0 && (
            <div className="py-10 text-center text-sm text-[#4D4D4D] italic">
              You have no saved reports for this project yet.
            </div>
          )}
          {!loading && !error && reports.length > 0 && (
            <div className="border border-gray-200 rounded-md max-h-[420px] overflow-y-auto divide-y divide-gray-200">
              {reports.map((r) => {
                const list       = schedulesByReport.get(r.id) || []
                const isRemoving = busy[`rep-${r.id}`] === 'remove'
                return (
                  <div key={r.id} className="bg-white">
                    {/* Report header — name (click-to-open) + Remove-report link */}
                    <div className="flex items-center gap-4 px-3 py-2.5 hover:bg-gray-50">
                      <button
                        type="button"
                        onClick={() => { onOpenReport?.(r.id); onClose?.() }}
                        className="text-sm font-semibold text-[#4D4D4D] text-left truncate hover:text-[#358ECD] flex-1 min-w-0"
                        title={r.name}
                      >
                        {r.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(r.id)}
                        disabled={isRemoving}
                        className="text-sm underline text-red-500 hover:text-red-700 disabled:opacity-50 shrink-0"
                      >
                        {isRemoving ? 'Deleting…' : 'Delete Report'}
                      </button>
                    </div>

                    {/* One indented sub-row per schedule (Email / FTP /
                        NDBC / GLOS). If the report has no schedules yet,
                        we render a small helper line so users know the
                        report exists but has nothing scheduled. */}
                    {list.length === 0 ? (
                      <div className="pl-8 pr-3 pb-2 -mt-1 text-xs text-gray-400 italic">
                        No schedules — set one up from Share or Export.
                      </div>
                    ) : (
                      <div className="pl-8 pr-3 pb-2 space-y-1">
                        {list.map((s) => {
                          const isToggling = busy[`sched-${s.id}`]    === 'toggle'
                          const isRmSched  = busy[`sched-rm-${s.id}`] === 'remove'
                          return (
                            <div key={s.id} className="flex items-center gap-4 text-sm">
                              <div className="w-24 shrink-0 text-xs text-[#4D4D4D] uppercase tracking-wide">
                                {TRANSPORT_LABEL[s.transport] || s.transport}
                              </div>
                              <div className="flex-1 min-w-0 text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                                {s.enabled && s.next_run_at
                                  ? `Next Run at ${fmtNextRun(s)}`
                                  : (s.enabled
                                      ? <span className="italic text-gray-400">No day/time selected</span>
                                      : <span className="italic text-gray-400">Disabled</span>)}
                              </div>
                              <div className="flex items-center gap-4 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleToggle(s)}
                                  disabled={isToggling}
                                  className="text-sm underline text-[#4D4D4D] hover:text-[#358ECD] disabled:opacity-50"
                                >
                                  {s.enabled ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSchedule(s)}
                                  disabled={isRmSched}
                                  className="text-sm underline text-red-500 hover:text-red-700 disabled:opacity-50"
                                >
                                  {isRmSched ? 'Removing…' : 'Remove'}
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* CLOSE button — full-width blue per the design */}
        <div className="px-5 pb-5 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="common-size15 w-full py-3 bg-[#358ECD] text-white text-sm font-bold tracking-wider rounded hover:bg-[#2A78B0] transition"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main exported component ──────────────────────────────────────────────────
export default function ReportView({
  mode,                  // 'view' | 'edit' | 'create' — 'list' is now a modal owned by the parent
  reportId,              // id of the report being viewed/edited (if any)
  selectedParameters = [], // parameters the user has checked in the dashboard's LEFT sidebar.
                          // The Settings → Data parameter picker reads from this so the
                          // report only ever pulls in parameters the user has scoped down
                          // to from the dashboard, instead of every parameter on the project.
  savedReports = [],     // current list of saved reports — used by Cancel on Create-mode
                          // to fall back to the first available report.
  onCancelCreate,        // () => void — invoked when Cancel is hit on Create mode AND no
                          // saved reports exist. Parent navigates the user away from the
                          // Report tab (back to the previously-active tab).
  onModeChange,          // (newMode, reportId?) => void
  onReportListChange,    // () => void — bump the parent's dropdown list
  onSelectedParametersChange, // (nextParams: SidebarParam[]) => void — pushed by
                          // View/Edit on load so the dashboard's left-sidebar
                          // checkboxes reflect what the report was saved with.
}) {
  const { projectId } = useParams()
  const rawTz = useAuthStore(s => s.user?.timezone)
  const customerTz = useMemo(() => resolveIanaTz(rawTz), [rawTz])

  // 'list' is no longer rendered here — it's now a modal (ReportListModal)
  // mounted by the parent. ReportView only handles view/edit/create.
  const [report,  setReport]  = useState(null)
  const [draft,   setDraft]   = useState(null)
  const [data,    setData]    = useState({})
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const [saving,  setSaving]  = useState(false)
  // Settings → Search Settings filter. Lifted here so we can reset it on
  // mode change and so the value survives sidebar re-renders.
  const [settingsSearch, setSettingsSearch] = useState('')
  // Header dropdown open state — Share, Export, Download.
  const [downloadOpen, setDownloadOpen] = useState(false)
  const [shareOpen,    setShareOpen]    = useState(false)
  const [exportOpen,   setExportOpen]   = useState(false)
  // Currently-open Export configuration modal: null | 'GLOS API' | 'NDBC XML'.
  const [exportDialog, setExportDialog] = useState(null)
  // Email / FTP share modal — shown when the user picks an option from
  // the Share dropdown. The backend isn't wired yet; we collect the form
  // and surface a toast so the user knows the request was recorded.
  const [shareDialog, setShareDialog]   = useState(null) // null | 'Email' | 'FTP/SFTP'
  // "View All Reports" — schedule list modal (Disable/Enable + Remove).
  const [scheduleListOpen, setScheduleListOpen] = useState(false)
  // Ref to the printable report panel — used to scope window.print() to
  // just the report content rather than the whole app chrome.
  const printRef = useRef(null)

  // Close the menus when the user clicks anywhere else on the page.
  useEffect(() => {
    if (!downloadOpen && !shareOpen && !exportOpen) return
    const onDoc = () => { setDownloadOpen(false); setShareOpen(false); setExportOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [downloadOpen, shareOpen, exportOpen])

  // View / Edit modes — load the named report.
  useEffect(() => {
    if (!reportId || mode === 'create') { setReport(null); return }
    let cancelled = false
    setLoading(true); setError(null)
    getReport(reportId)
      .then(r => {
        if (cancelled) return
        setReport(r)
        setDraft({ name: r.name, ...DEFAULT_SETTINGS, ...(r.settings || {}) })
        // Push the report's saved parameters into the dashboard's left
        // sidebar so the checkboxes match what the report was
        // configured with. Without this, the sync effect below (which
        // treats the sidebar as source of truth) immediately overwrites
        // the loaded parameters with whatever the sidebar currently
        // has, and the user sees the wrong checkboxes + can't tweak
        // the saved config.
        const savedParams = (r.settings?.parameters || [])
        if (savedParams.length && typeof onSelectedParametersChange === 'function') {
          onSelectedParametersChange(savedParams)
        }
      })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load report') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, mode])

  // Create mode — start a clean draft, no server round-trip yet. Default
  // range is "Today" so From/To pre-fill to start-of-day → now. Parameters
  // are seeded from whatever the user has selected in the dashboard's
  // left sidebar; the auto-sync effect below keeps it that way as the
  // sidebar selection changes.
  useEffect(() => {
    if (mode !== 'create') return
    setReport(null)
    // A brand-new report has no Timezone setting yet, so "Today" is the
    // account's today.
    const range = rangeFromPreset('Today', customerTz) || { from: '', to: '' }
    setDraft({
      name: 'Report 1',
      ...DEFAULT_SETTINGS,
      dateRange: 'Today',
      dateFrom: range.from,
      dateTo:   range.to,
      parameters: (selectedParameters || []).map((p) => ({
        id: p.id, name: p.name, unit: p.unit || '',
        siteName: p.siteName || p.site_name || '',
        deviceName: p.deviceName || p.device_name || '',
        sensorName: p.sensorName || p.sensor_name || '',
      })),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // Keep `draft.parameters` synced to the dashboard's left-sidebar
  // selection while the user is editing or creating a report. The
  // sidebar is the single source of truth — without this, the report
  // would diverge from whatever the user has scoped to.
  useEffect(() => {
    if (mode !== 'edit' && mode !== 'create') return
    setDraft(d => {
      if (!d) return d
      const next = (selectedParameters || []).map((p) => ({
        id: p.id, name: p.name, unit: p.unit || '',
        siteName: p.siteName || p.site_name || '',
        deviceName: p.deviceName || p.device_name || '',
        sensorName: p.sensorName || p.sensor_name || '',
      }))
      const prevKey = (d.parameters || []).map(p => p.id).join(',')
      const nextKey = next.map(p => p.id).join(',')
      if (prevKey === nextKey) return d
      return { ...d, parameters: next }
    })
  }, [selectedParameters, mode])

  // The Settings sidebar reads its parameter list straight from the
  // dashboard's left-sidebar selection (`selectedParameters`), no longer
  // from a fresh project-sites fetch. That keeps the report's available
  // parameters in lockstep with whatever the user has scoped down to.

  // Pull the chart data + stats for the report's chosen parameters.
  // Run in view AND edit so the user sees a live preview while editing.
  const activeSettings = mode === 'view' ? (report?.settings || {}) : (draft || {})
  const params         = activeSettings.parameters || []
  const dateFrom       = activeSettings.dateFrom
  const dateTo         = activeSettings.dateTo
  const paramKey       = params.map(p => p.id).join(',')

  // The report's own Timezone setting drives the data window, the on-screen
  // display, AND every share/export — not the signed-in user's profile zone.
  // Empty / "Use account timezone" falls back to the profile timezone.
  const reportTz = useMemo(() => {
    const t = (activeSettings?.timezone || '').trim()
    if (!t || t === 'Use account timezone') return customerTz
    return resolveIanaTz(t) || customerTz
  }, [activeSettings?.timezone, customerTz])

  useEffect(() => {
    if (!params.length) { setData({}); return }
    let cancelled = false
    // The new Data section uses `<input type="datetime-local">` which
    // yields strings like '2026-03-16T11:59'. Convert to the
    // 'YYYY-MM-DD HH:MM:SS' the graph-data API expects. If only a plain
    // date came in (legacy reports saved before this change), pad with
    // 00:00:00 / 23:59:59 as before.
    const toApiTs = (s, end) => {
      if (!s) return undefined
      if (s.includes('T')) return `${s.replace('T', ' ')}:00`
      return end ? `${s} 23:59:59` : `${s} 00:00:00`
    }
    const fromStr = toApiTs(dateFrom, false)
    const toStr   = toApiTs(dateTo,   true)
    Promise.all(params.map(p =>
      projectsApi.fetchGraphData(Number(p.id), {
        fromDate: fromStr, toDate: toStr, timezone: reportTz,
      }).then(res => ({ p, res })).catch(e => ({ p, error: e }))
    )).then(results => {
      if (cancelled) return
      const next = {}
      for (const { p, res } of results) {
        const points = Array.isArray(res?.data) ? res.data : []
        const ys = points.filter(pt => pt.y != null).map(pt => Number(pt.y)).filter(v => Number.isFinite(v))
        next[p.id] = { points, stats: computeStats(ys) }
      }
      setData(next)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey, dateFrom, dateTo, reportTz, mode])

  // ── Actions ──
  const handleSave = async () => {
    if (!draft) return
    setSaving(true); setError(null)
    try {
      const { name, ...rest } = draft
      if (mode === 'create') {
        const created = await createReport({
          projectId: Number(projectId),
          name: name || 'Report 1',
          settings: rest,
        })
        onReportListChange?.()
        onModeChange?.('view', created.id)
      } else {
        const updated = await updateReport(reportId, { name, settings: rest })
        setReport(updated)
        onReportListChange?.()
        onModeChange?.('view', reportId)
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save report')
    } finally {
      setSaving(false)
    }
  }
  // ── Download handlers ────────────────────────────────────────────────
  const safeName = (s) => (s || 'report').replace(/[^a-z0-9_\-]+/gi, '_')
  const handleDownloadCsv = () => {
    setDownloadOpen(false)
    const settings = report?.settings || draft || {}
    const params   = settings.parameters || []
    if (!params.length) { alert('No parameters selected — add some in the Settings → Data section.'); return }
    const csv = buildDataTableCsv(params, data, reportTz, settings)
    downloadBlob(`${safeName(report?.name || draft?.name || 'report')}.csv`, csv)
  }
  const handleDownloadPdf = async () => {
    setDownloadOpen(false)
    try {
      await buildReportPdf({
        settings:   report?.settings || draft || {},
        params:     activeSettings.parameters || [],
        data,
        customerTz: reportTz,
        filename:   `${safeName(report?.name || draft?.name || 'report')}.pdf`,
      })
    } catch (e) {
      alert('Could not generate PDF: ' + (e?.message || e))
    }
  }

  // ── Share handlers — open a small modal for each transport. Backend
  // delivery is a future task; for now we capture the form and toast a
  // confirmation so the user knows the action was recorded.
  const handleShareEmail = () => { setShareOpen(false); setShareDialog('Email') }
  const handleShareFtp   = () => { setShareOpen(false); setShareDialog('FTP/SFTP') }

  // Export handlers — these open the configuration modal that lets the
  // user map the report's parameters onto the destination format's
  // schema (GLOS standard names, NDBC variables) before the actual
  // push is made.
  const handleExportGlos = () => { setExportOpen(false); setExportDialog('GLOS API') }
  const handleExportNdbc = () => { setExportOpen(false); setExportDialog('NDBC XML') }

  const handleCancel = () => {
    // Edit mode → discard pending changes, return to view of the same id.
    if (mode === 'edit') { onModeChange?.('view', reportId); return }
    // Create mode → if any saved report exists, open the first one so the
    // user lands on something real instead of an empty Report tab.
    // Otherwise bounce them back to the tab they came from.
    if (mode === 'create') {
      if (savedReports && savedReports.length > 0) {
        onModeChange?.('view', savedReports[0].id)
      } else if (onCancelCreate) {
        onCancelCreate()
      } else {
        onModeChange?.(null)
      }
      return
    }
    onModeChange?.(null)
  }

  // ── View + Edit + Create modes ──
  // When mode is missing (e.g. user just opened the Report tab without
  // choosing a saved report yet), render an empty-state prompt instead of
  // crashing on undefined draft / report.
  if (!mode) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-8 text-center max-[767px]:flex-wrap max-[767px]:p-4">
        Open a saved report from the <strong className="mx-1">Report</strong> menu, or pick
        <strong className="mx-1">+ Create Report</strong> to start a new one.
      </div>
    )
  }
  const isEdit     = mode === 'edit' || mode === 'create'
  const headerName = (isEdit ? draft?.name : report?.name) || 'Report 1'
  const range      = fmtRange(activeSettings.dateFrom, activeSettings.dateTo)

  return (
    // Outer container fills the available width. In edit mode the layout
    // is a column — Save/Cancel sit pinned at the top-right, then the
    // report panel + Settings sidebar share the row beneath. View mode is
    // a single full-width row (no sidebar, no Save/Cancel).
    <div className={`flex-1 flex flex-col w-full overflow-hidden ${isEdit ? 'gap-3 p-4' : 'p-2'} report-root`}>
      {/* Print-mode CSS — hides everything outside the printable report
          container (the toolbar, dropdowns, the Settings sidebar, etc.)
          and resets the body so window.print() only emits the report. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .report-printable, .report-printable * { visibility: visible !important; }
          .report-printable { position: absolute !important; inset: 0 !important; padding: 0 !important; box-shadow: none !important; border: none !important; }
          .no-print { display: none !important; }
          @page { size: ${(report?.settings || draft || {}).pdfFormat?.paperSize?.startsWith('A4') ? 'A4' :
                          (report?.settings || draft || {}).pdfFormat?.paperSize?.startsWith('Legal') ? 'legal' :
                          (report?.settings || draft || {}).pdfFormat?.paperSize?.startsWith('Tabloid') ? 'tabloid' : 'letter'}
                       ${(report?.settings || draft || {}).pdfFormat?.orientation === 'Landscape' ? 'landscape' : 'portrait'};
                  margin: 0.5in; }
        }
      `}</style>

      {/* Edit-mode Save / Cancel sit at the very top, right-aligned —
          matches the XD design (was previously stacked above the
          Settings sidebar). View mode hides this row entirely. */}
      {/* Top action bar — sits on the gray page background ABOVE the
          white report panel. Edit mode shows Save + Cancel; view mode
          shows Share / Export / Download / Edit. */}
      {isEdit ? (
        <div className="flex justify-end items-center gap-2 no-print">
          <button onClick={handleSave} disabled={saving}
            className="common-size15 px-4 py-2 bg-[#358ECD] text-white text-sm font-semibold rounded-lg inline-flex items-center gap-1 hover:bg-[#2A78B0] disabled:opacity-50">
            {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
          <button onClick={handleCancel} disabled={saving}
            className="common-size15 px-4 py-2 bg-[#FFFFFF] rounded-lg text-[#4D4D4D] text-sm hover:underline disabled:opacity-50">
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex justify-end items-center gap-2 no-print mb-3 max-[767px]:flex-wrap max-[767px]:justify-center">
          {/* Share → Email / FTP-SFTP (delivery to recipients/storage) */}
          <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setShareOpen(o => !o); setDownloadOpen(false); setExportOpen(false) }}
              className="px-4 py-2 bg-[#358ECD] text-white text-sm rounded-lg inline-flex items-center gap-1.5 hover:bg-[#2A78B0]">
              <Share2 className="w-4 h-4" /> Share
            </button>
            {shareOpen && (
              <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-20 text-sm">
                <button onClick={handleShareEmail} className="block w-full text-left px-3 py-2 hover:bg-gray-50">Email</button>
                <button onClick={handleShareFtp}   className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-t border-gray-100">FTP/SFTP</button>
              </div>
            )}
          </div>
          {/* Export → external observation networks (NDBC, GLOS). */}
          <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setExportOpen(o => !o); setDownloadOpen(false); setShareOpen(false) }}
              className="px-4 py-2 bg-[#358ECD] text-white text-sm rounded-lg inline-flex items-center gap-1.5 hover:bg-[#2A78B0]">
              <Upload className="w-4 h-4" /> Export
            </button>
            {exportOpen && (
              <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-20 text-sm p-4 mt-4">
                <button onClick={handleExportNdbc} className="common-size15 block w-full text-left px-3 py-2 hover:bg-gray-50">NDBC XML</button>
                <button onClick={handleExportGlos} className="common-size15 block w-full text-left px-3 py-2 hover:bg-gray-50 border-gray-100">GLOS API</button>
              </div>
            )}
          </div>
          {/* Download → local file (CSV data table / PDF render) */}
          <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setDownloadOpen(o => !o); setShareOpen(false); setExportOpen(false) }}
              className="px-4 py-2 bg-[#358ECD] text-white text-sm rounded-lg inline-flex items-center gap-1.5 hover:bg-[#2A78B0]">
              <Download className="w-4 h-4" /> Download
            </button>
            {downloadOpen && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 text-sm">
                <button onClick={handleDownloadCsv} className="block w-full text-left px-3 py-2 hover:bg-gray-50">CSV (Data Table Only)</button>
                <button onClick={handleDownloadPdf} className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-t border-gray-100">PDF</button>
              </div>
            )}
          </div>
          {/* View all report/share schedules configured on this project. */}
          <button
            onClick={() => setScheduleListOpen(true)}
            title="View all scheduled reports"
            className="px-3 py-2 bg-white border border-gray-300 text-sm text-[#4D4D4D] rounded-lg hover:bg-gray-50"
          >
            View All
          </button>
          <button onClick={() => onModeChange?.('edit', reportId)} title="Edit"
            className="p-2 bg-white border border-gray-300 rounded-lg text-[#4D4D4D] hover:text-[#358ECD] hover:bg-gray-50">
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className={`flex-1 flex w-full min-h-0 max-[767px]:flex-wrap overflow-hidden ${isEdit ? 'gap-4' : ''}`}>
      <div ref={printRef} className="flex-1 min-w-0 flex flex-col bg-white border-gray-200 rounded-lg overflow-hidden report-printable max-[767px]:w-full">
        {/* Title strip — inside the white panel. Holds the report name
            on the left and the active date range on the right. */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between no-print">
          <div className="common-size18 text-sm font-bold text-[#4D4D4D]">Report: {headerName}</div>
          <div className="flex items-center gap-2">
            <span className="common-size18 text-xs text-[#4D4D4D]">{range}</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {isEdit && (!draft?.parameters || draft.parameters.length === 0) ? (
            <div className="h-full min-h-[260px] flex items-center justify-center text-sm text-gray-400">
              Select Parameter(s) to Start Report
            </div>
          ) : (
            <ReportContent
              settings={isEdit ? draft : report?.settings}
              data={data}
              loading={loading}
              customerTz={reportTz}
            />
          )}
        </div>
      </div>

      {isEdit && draft && (
        <SettingsSidebar
          draft={draft}
          setDraft={setDraft}
          sidebarParameters={selectedParameters}
          searchQuery={settingsSearch}
          setSearchQuery={setSettingsSearch}
        />
      )}
      </div>{/* /row */}

      {/* Share dialog — minimal form for Email / FTP-SFTP. The actual
          delivery is a future backend; submit just records the intent. */}
      {shareDialog && (
        <ShareDialog
          reportId={reportId}
          channel={shareDialog}
          reportName={report?.name || draft?.name || 'Report'}
          data={data}
          settings={activeSettings}
          params={activeSettings.parameters || []}
          reportTz={reportTz}
          onClose={() => setShareDialog(null)}
        />
      )}

      {/* Export dialog — GLOS API and NDBC XML each pop a real
          configuration modal that maps the report's parameters onto the
          destination's schema before submission. */}
      {exportDialog === 'GLOS API' && (
        <GlosApiExportDialog
          reportId={reportId}
          reportName={report?.name || draft?.name || 'Report'}
          parameters={(report?.settings?.parameters || draft?.parameters || [])}
          reportTzLabel={activeSettings?.timezone || rawTz || ''}
          onClose={() => setExportDialog(null)}
        />
      )}
      {exportDialog === 'NDBC XML' && (
        <NdbcXmlExportDialog
          reportId={reportId}
          reportName={report?.name || draft?.name || 'Report'}
          parameters={(report?.settings?.parameters || draft?.parameters || [])}
          reportTzLabel={activeSettings?.timezone || rawTz || ''}
          onClose={() => setExportDialog(null)}
        />
      )}

      {/* Report → View All — list every schedule configured on this project. */}
      {scheduleListOpen && (
        <ReportScheduleListModal
          projectId={projectId}
          onClose={() => setScheduleListOpen(false)}
        />
      )}
    </div>
  )
}

// ─── Share dialog ─────────────────────────────────────────────────────────────
// Modal opened from the Share menu (Email / FTP-SFTP). Both channels
// POST to real backends now:
//   Email → /api/data/reports/<id>/share/email/  sends a CSV attachment
//           via Django/SendGrid.
//   FTP   → /api/data/reports/<id>/share/ftp/    uploads a CSV to the
//           supplied FTP host (FTPS when the TLS box is ticked).
function ShareDialog({ reportId, channel, reportName, data, settings, params, reportTz, onClose }) {
  const [submitting, setSubmitting] = useState(false)
  const [done,       setDone]       = useState(false)
  const [error,      setError]      = useState(null)
  const [result,     setResult]     = useState(null)
  const customerTz = resolveIanaTz(useAuthStore(s => s.user?.timezone))
  // Every share/export uses the report's timezone (falls back to the account
  // timezone when the report is set to "Use account timezone").
  const tz = reportTz || customerTz
  const [form,       setForm]       = useState(
    channel === 'Email'
      ? { filename: '', to: '', subject: `Report: ${reportName}`, body: '', send_format: 'csv' }
      : { filename: '', host: '', port: '22', username: '', password: '', path: '/', protocol: 'sftp', send_format: 'csv' }
  )
  // Action = Send Now | Schedule. Schedule adds the ScheduleForm below the
  // transport fields; on submit we POST to /reports/<id>/schedules/
  // instead of the immediate send endpoint.
  const [action,   setAction]   = useState('now')
  const [schedule, setSchedule] = useState(emptyScheduleValue)
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Build the exact same file the Download menu produces, so the emailed /
  // uploaded attachment is byte-identical to what the user would download
  // (same condense/filter/tz + chart for PDF). Returns the attachment_* fields
  // the backend uses verbatim, or null when there's nothing to build (no
  // params / no data) so the backend falls back to server-side generation.
  const buildAttachmentFields = async (fmt) => {
    if (!params?.length || !data || !Object.keys(data).length) return null
    const raw  = (form.filename || reportName || 'report')
    const base = raw.replace(/[^a-z0-9_\-]+/gi, '_')
    if (fmt === 'pdf') {
      const blob = await buildReportPdf({ settings, params, data, customerTz: tz, returnBlob: true })
      return {
        attachment_base64:   await blobToBase64(blob),
        attachment_filename: base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`,
        attachment_mime:     'application/pdf',
      }
    }
    const csv = buildDataTableCsv(params, data, tz, settings)
    return {
      // encodeURIComponent→unescape keeps multi-byte chars intact through btoa.
      attachment_base64:   btoa(unescape(encodeURIComponent(csv))),
      attachment_filename: base.toLowerCase().endsWith('.csv') ? base : `${base}.csv`,
      attachment_mime:     'text/csv; charset=utf-8',
    }
  }

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!reportId) { setError('Save the report first before sharing.'); return }
    setError(null); setSubmitting(true)
    try {
      if (action === 'schedule') {
        // Persist a ReportSchedule row — the runner picks it up on the
        // next tick and hands off to the same share/upload endpoints.
        const transport = channel === 'Email' ? 'email' : 'ftp'
        const config = channel === 'Email'
          ? { recipients: form.to, subject: form.subject, body: form.body, send_format: form.send_format || 'csv', filename: form.filename || '' }
          : {
              host:        form.host.trim(),
              port:        Number(form.port) || (form.protocol === 'sftp' ? 22 : 21),
              user:        form.username.trim(),
              password:    form.password,
              directory:   form.path.trim(),
              protocol:    form.protocol || 'sftp',
              filename:    form.filename || '',
              send_format: form.send_format || 'csv',
            }
        const resp = await createSchedule(reportId, {
          transport,
          filename:      form.filename || '',
          send_format:   form.send_format || 'csv',
          enabled:       !!schedule.enabled,
          only_new_data: !!schedule.only_new_data,
          days_of_week:  schedule.days_of_week,
          schedule_type: schedule.schedule_type,
          send_at:       schedule.send_at,
          // Interpret the picked send_at slots in the user's profile
          // timezone (server converts to UTC for storage + firing).
          timezone:      customerTz,
          config,
        })
        setResult({ scheduled: true, schedule_id: resp?.id, next_run_at: resp?.next_run_at })
        setDone(true)
        return
      }

      // Build the attachment client-side (same code path as Download) and
      // ship the bytes so the delivered file matches the download exactly.
      // If it can't be built, we omit the fields and the backend generates
      // its own copy as before.
      const attach = await buildAttachmentFields(form.send_format || 'csv')

      let resp
      if (channel === 'Email') {
        resp = await shareReportEmail(reportId, {
          recipients:  form.to,
          subject:     form.subject,
          body:        form.body,
          send_format: form.send_format || 'csv',
          filename:    form.filename || '',
          timezone:    tz,   // report timezone — keeps email text/filename consistent with the attachment
          ...(attach || {}),
        })
      } else {
        resp = await shareReportFtp(reportId, {
          host:        form.host.trim(),
          port:        Number(form.port) || (form.protocol === 'sftp' ? 22 : 21),
          user:        form.username.trim(),
          password:    form.password,
          directory:   form.path.trim(),
          protocol:    form.protocol || 'sftp',
          filename:    form.filename || '',
          send_format: form.send_format || 'csv',
          timezone:    tz,   // report timezone
          ...(attach || {}),
        })
      }
      setResult(resp || {})
      setDone(true)
    } catch (e2) {
      setError(e2?.response?.data?.message || e2?.message || 'Delivery failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // No backdrop onClick: this form holds typed recipients, credentials and a
    // message, so a stray click outside must not discard it. Closing is the X
    // (or Cancel) only.
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 no-print">
      <div className={`bg-white rounded-lg shadow-2xl w-full ${channel === 'Email' ? 'max-w-xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col`}
           onClick={(e) => e.stopPropagation()}>
        <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Share via {channel}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        {done ? (
          <div className="px-5 py-6 text-sm text-[#4D4D4D]">
            {result?.scheduled ? (
              <>
                <div className="inline-flex items-center gap-2 text-green-700 mb-2">
                  <Check className="w-4 h-4" /> Schedule saved.
                </div>
                <p className="text-xs text-gray-600">
                  Next run:{' '}
                  <span className="">
                    {result?.next_run_at || 'no valid day/time picked — enable a day and add a time slot to activate.'}
                  </span>
                </p>
                <p className="text-xs text-[#4D4D4D] mt-1">
                  Manage all schedules from the <strong>View All</strong> button on the Report toolbar.
                </p>
              </>
            ) : channel === 'Email' ? (
              <>
                <div className="inline-flex items-center gap-2 text-green-700 mb-2">
                  <Check className="w-4 h-4" /> Email sent.
                </div>
                <ul className="mt-1 text-xs text-[#4D4D4D] space-y-0.5">
                  <li><span className="text-[#4D4D4D]">Recipients:</span> {(result?.sent_to || []).join(', ')}</li>
                  <li><span className="text-[#4D4D4D]">Attachment:</span> <code>{result?.filename}</code></li>
                  <li><span className="text-[#4D4D4D]">Size:</span> {result?.bytes_sent} bytes</li>
                </ul>
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 text-green-700 mb-2">
                  <Check className="w-4 h-4" /> Uploaded via {(result?.upload?.protocol || 'ftp').toUpperCase()}.
                </div>
                <ul className="mt-1 text-xs text-[#4D4D4D] space-y-0.5">
                  <li><span className="text-[#4D4D4D]">Server:</span> {result?.upload?.ftp_host}</li>
                  <li><span className="text-[#4D4D4D]">Directory:</span> {result?.upload?.ftp_directory || '/'}</li>
                  <li><span className="text-[#4D4D4D]">File:</span> <code>{result?.upload?.filename}</code></li>
                  <li><span className="text-[#4D4D4D]">Size:</span> {result?.upload?.bytes_uploaded} bytes</li>
                  <li><span className="text-[#4D4D4D]">Protocol:</span> {(result?.upload?.protocol || 'ftp').toUpperCase()}</li>
                </ul>
              </>
            )}
            <div className="mt-4 text-right">
              <button onClick={onClose}
                className="px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded hover:bg-[#2A78B0]">
                OK
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col flex-1 min-h-0 text-sm">
            <div className="px-8 py-6 space-y-3 overflow-y-auto">
            {error && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}

            {channel === 'Email' ? (
              <>
                {/* Filename — used as the CSV/PDF name in the delivered file.
                    Blank falls back to the report name on the server. */}
                <label className="block">
                  <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Filename</span>
                  <input
                    type="text" value={form.filename}
                    onChange={(e) => update('filename', e.target.value)}
                    placeholder="Default"
                    className="mt-2 mb-3 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                  />
                </label>
                <label className="block">
                  <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Recipient(s)</span>
                  <input
                    type="text" required value={form.to}
                    onChange={(e) => update('to', e.target.value)}
                    placeholder="alice@example.com, bob@example.com"
                    className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                  />
                </label>
                <label className="block">
                  <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Subject</span>
                  <input
                    type="text" value={form.subject}
                    onChange={(e) => update('subject', e.target.value)}
                    className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                  />
                </label>
                <label className="block">
                  <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Message (optional)</span>
                  <textarea rows={3} value={form.body}
                    onChange={(e) => update('body', e.target.value)}
                    className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD] resize-y" />
                </label>

                {/* Send Report As */}
                <label className="block">
                  <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Send Report As</span>
                  <select
                    value={form.send_format}
                    onChange={(e) => update('send_format', e.target.value)}
                    className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-[#358ECD]"
                  >
                    <option value="csv">CSV (Data Table Only)</option>
                    <option value="pdf">PDF</option>
                  </select>
                </label>

                {/* Action */}
                <label className="block">
                  <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Action</span>
                  <select
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-[#358ECD]"
                  >
                    <option value="now">Send Now</option>
                    <option value="schedule">Schedule</option>
                  </select>
                </label>
                {action === 'schedule' && (
                  <div className="pt-1"><ScheduleForm value={schedule} onChange={setSchedule} /></div>
                )}
              </>
            ) : (
              <>
                {/* FTP form — 2-column grid. Left column: connection.
                    Right column: destination + delivery options. */}
                <div className="flex flex-col">
                  {/* ─── Left column ─────────────────────── */}
                  <div className="space-y-3">
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Filename</span>
                      <input
                        type="text" value={form.filename}
                        onChange={(e) => update('filename', e.target.value)}
                        placeholder="Default"
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                      />
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <label className="block col-span-2">
                        <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Host</span>
                        <input
                          type="text" required value={form.host}
                          onChange={(e) => update('host', e.target.value)}
                          placeholder="ftp.example.com"
                          className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                        />
                      </label>
                      <label className="block">
                        <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Port</span>
                        <input
                          type="text" value={form.port}
                          onChange={(e) => update('port', e.target.value)}
                          className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                        />
                      </label>
                    </div>
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Username</span>
                      <input
                        type="text" required value={form.username}
                        onChange={(e) => update('username', e.target.value)}
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                      />
                    </label>
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Password</span>
                      <input
                        type="password" required value={form.password}
                        onChange={(e) => update('password', e.target.value)}
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                      />
                    </label>
                  </div>

                  {/* ─── Right column ────────────────────── */}
                  <div className="space-y-3">
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Remote Path</span>
                      <input
                        type="text" value={form.path}
                        onChange={(e) => update('path', e.target.value)}
                        placeholder="/reports/"
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-[#358ECD]"
                      />
                    </label>
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Protocol</span>
                      <select
                        value={form.protocol || 'sftp'}
                        onChange={(e) => {
                          const proto = e.target.value
                          setForm(f => {
                            const defaultPort = proto === 'sftp' ? '22' : '21'
                            const port = (f.port === '21' || f.port === '22' || f.port === '')
                              ? defaultPort : f.port
                            return { ...f, protocol: proto, port }
                          })
                        }}
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-[#358ECD]"
                      >
                        <option value="sftp">SFTP (SSH — port 22)</option>
                        <option value="ftp">FTP (port 21)</option>
                        <option value="ftps">FTPS — Explicit TLS (port 21)</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Send Report As</span>
                      <select
                        value={form.send_format}
                        onChange={(e) => update('send_format', e.target.value)}
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-[#358ECD]"
                      >
                        <option value="csv">CSV (Data Table Only)</option>
                        <option value="pdf">PDF</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="common-size15 text-xs font-bold text-[#4D4D4D]">Action</span>
                      <select
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                        className="mt-2 mb-3 w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:border-[#358ECD]"
                      >
                        <option value="now">Send Now</option>
                        <option value="schedule">Schedule</option>
                      </select>
                    </label>
                  </div>
                </div>

                {/* Schedule block spans full width when active — the day
                    checkboxes + time-slot grid need the room. */}
                {action === 'schedule' && (
                  <div className="pt-1 border-t border-gray-100 mt-3">
                    <ScheduleForm value={schedule} onChange={setSchedule} />
                  </div>
                )}
              </>
            )}
            </div>

            <div className="px-6 py-4 bg-white">
              <button type="submit" disabled={submitting}
                className="savebtn common-size18 w-full px-4 py-1.5 text-sm text-white uppercase font-bold bg-[#358ECD] rounded-md hover:bg-[#2d7ab8] transition">
                {submitting && <Loader className="w-3.5 h-3.5 animate-spin" />}
                {action === 'schedule' ? 'Schedule' : 'Send'}
              </button>
              <button type="button" onClick={onClose}
                className="cancelbtn common-size16 w-full px-4 py-1.5 text-sm text-[#358ECD] font-semibold hover:underline border-gray-200 rounded-md transition">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ─── GLOS API export dialog ───────────────────────────────────────────────────
// Configuration form for the "Export → GLOS API" flow. The user fills in:
//   • General — Name, Description, Platform ID, Platform ID Type
//   • Date Range Configuration — Timezone, Date Range, Time Sort
//   • Parameter Configuration — for each report parameter, choose a GLOS
//     Standard Name (from the allow-list below) and a Depth Value.
//
// Submit POSTs to /api/data/reports/<id>/export/glos/, where the server
// builds the upstream payload, attaches the API key (kept server-side),
// and forwards to staging-seagull-api.glos.org.

// Canonical GLOS observation variables — kept in sync with the GLOS API's
// `observations.variable_name` field. Updating the list here only affects
// the dropdown's choices; the actual mapping happens server-side.
const GLOS_STANDARD_NAMES = [
    'sea_surface_temperature',
    'sea_water_temperature',
    'sea_water_temperature_1',
    'sea_water_temperature_2',
    'sea_water_temperature_3',
    'sea_water_temperature_4',
    'sea_water_temperature_5',
    'sea_water_temperature_6',
    'sea_water_temperature_7',
    'sea_water_temperature_8',
    'sea_water_temperature_9',
    'air_temperature',
    'air_pressure',
    'relative_humidity',
    'sea_surface_wave_from_direction',
    'sea_surface_wave_maximum_height',
    'sea_surface_wave_mean_period',
    'sea_water_turbidity',
    'wind_from_direction',
    'wind_speed',
]

// Depth presets — GLOS expects sensor depth (meters, positive downward).
// "None" lets the user opt out for sensors that don't have a depth (air
// temperature, wind, etc.).
const GLOS_DEPTH_OPTIONS = ['None', '0', '0.5', '1', '2', '3', '5', '10']

function GlosApiExportDialog({ reportId, reportName, parameters, reportTzLabel, onClose }) {
    const customerTz = resolveIanaTz(useAuthStore(s => s.user?.timezone))
    const [general, setGeneral] = useState({
        name:           reportName || '',
        description:    '',
        platformId:     '',
        platformIdType: 'GLOS (org)',
    })
    const [dateCfg, setDateCfg] = useState({
        // Default the export timezone to the report's own timezone setting so
        // exports follow it; fall back to UTC when the report uses the account tz.
        timezone:  (reportTzLabel && reportTzLabel !== 'Use account timezone') ? reportTzLabel : '(UTC+00:00) UTC',
        dateRange: 'Today',
        timeSort:  'Ascending',
    })
    // Per-parameter mapping — keyed by parameter id. Defaults to the
    // first GLOS standard name so the dropdown isn't empty; user must
    // still confirm before submit.
    const [mapping, setMapping] = useState(() => {
        const init = {}
        for (const p of parameters || []) {
            init[p.id] = { standard_name: GLOS_STANDARD_NAMES[0], depth: 'None' }
        }
        return init
    })
    const [submitting, setSubmitting] = useState(false)
    const [done,       setDone]       = useState(false)
    const [scheduled,  setScheduled]  = useState(false)
    const [error,      setError]      = useState(null)
    const [action,     setAction]     = useState('now')      // 'now' | 'schedule'
    const [schedule,   setSchedule]   = useState(emptyScheduleValue)

    const updateMap = (pid, patch) =>
        setMapping((m) => ({ ...m, [pid]: { ...(m[pid] || {}), ...patch } }))

    const submit = async (e) => {
        e?.preventDefault?.()
        if (!general.platformId.trim()) {
            setError('Platform ID is required.'); return
        }
        if (!reportId) {
            setError('Save the report first before exporting.'); return
        }
        setSubmitting(true); setError(null)
        try {
            const body = {
                general: {
                    name:             general.name,
                    description:      general.description,
                    platform_id:      general.platformId.trim(),
                    platform_id_type: general.platformIdType,
                },
                date_range: dateCfg,
                parameter_mappings: parameters.map((p) => ({
                    parameter_id:  p.id,
                    parameter_name: p.name,
                    parameter_unit: p.unit || '',
                    standard_name: mapping[p.id]?.standard_name || GLOS_STANDARD_NAMES[0],
                    depth:         mapping[p.id]?.depth         || 'None',
                })),
            }
            if (action === 'schedule') {
                await createSchedule(reportId, {
                    transport:     'glos',
                    enabled:       !!schedule.enabled,
                    only_new_data: !!schedule.only_new_data,
                    days_of_week:  schedule.days_of_week,
                    schedule_type: schedule.schedule_type,
                    timezone:      customerTz,
                    send_at:       schedule.send_at,
                    config:        body,   // full GLOS payload stored verbatim
                })
                setScheduled(true)
            } else {
                await exportReportGlos(reportId, body)
            }
            setDone(true)
        } catch (e2) {
            setError(e2?.response?.data?.message || e2?.message || 'Failed to export to GLOS')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        // No backdrop onClick — see the Share dialog above. Losing a filled-in
        // GLOS configuration to a misplaced click is worse than an extra click
        // on the X.
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 no-print">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Share Report: GLOS API</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>

                {done ? (
                    <div className="px-5 py-8 text-sm text-[#4D4D4D] flex-1">
                        <div className="inline-flex items-center gap-2 text-green-700 mb-2">
                            <Check className="w-4 h-4" /> Submitted to GLOS.
                        </div>
                        <p>Your observations have been queued at <code className="text-[#4D4D4D]">staging-seagull-api.glos.org</code>.</p>
                        <div className="mt-6 text-right">
                            <button onClick={onClose} className="px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded hover:bg-[#2A78B0]">OK</button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="flex-1 min-h-0 overflow-y-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 px-8 py-6">
                            {/* LEFT: General + Date Range */}
                            <div>
                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3">General</h3>
                                <ExpLabel>
                                    <input type="text" value={general.name}
                                        onChange={(e) => setGeneral((g) => ({ ...g, name: e.target.value }))}
                                        placeholder="Name" className={ExpInputCls} />
                                </ExpLabel>
                                <ExpLabel>
                                    <textarea rows={3} value={general.description}
                                        onChange={(e) => setGeneral((g) => ({ ...g, description: e.target.value }))}
                                        placeholder="Description"
                                        className={`${ExpInputCls} resize-y`} />
                                </ExpLabel>
                                <ExpLabel label="Platform ID">
                                    <input type="text" value={general.platformId}
                                        onChange={(e) => setGeneral((g) => ({ ...g, platformId: e.target.value }))}
                                        className={ExpInputCls} />
                                </ExpLabel>
                                <ExpLabel label="Platform ID Type">
                                    <select value={general.platformIdType}
                                        onChange={(e) => setGeneral((g) => ({ ...g, platformIdType: e.target.value }))}
                                        className={`${ExpInputCls} bg-white cstm-br`}>
                                        <option>GLOS (org)</option>
                                        <option>WMO</option>
                                        <option>NDBC</option>
                                        <option>Other</option>
                                    </select>
                                </ExpLabel>

                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mt-5 mb-3 border-t pt-4">Date Range Configuration</h3>
                                <ExpLabel label="Timezone">
                                    <select value={dateCfg.timezone}
                                        onChange={(e) => setDateCfg((d) => ({ ...d, timezone: e.target.value }))}
                                        className={`${ExpInputCls} bg-white cstm-br`}>
                                        {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                                    </select>
                                </ExpLabel>
                                <ExpLabel label="Date Range">
                                    <select value={dateCfg.dateRange}
                                        onChange={(e) => setDateCfg((d) => ({ ...d, dateRange: e.target.value }))}
                                        className={`${ExpInputCls} bg-white cstm-br`}>
                                        {DATE_RANGE_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </ExpLabel>
                                <ExpLabel label="Time Sort">
                                    <select value={dateCfg.timeSort}
                                        onChange={(e) => setDateCfg((d) => ({ ...d, timeSort: e.target.value }))}
                                        className={`${ExpInputCls} bg-white cstm-br`}>
                                        <option>Ascending</option>
                                        <option>Descending</option>
                                    </select>
                                </ExpLabel>
                                {/* Action — Send Now (immediate) or Schedule (recurring
                                    push to GLOS on the picked days/times). */}
                                <div className="text-sm mt-5 mb-3 border-t pt-4">
                                    <label className="block text-sm">
                                        <span className="common-size18 text-sm font-bold text-[#4D4D4D] block mb-2">Action</span>
                                        <select
                                            value={action}
                                            onChange={(e) => setAction(e.target.value)}
                                            className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded bg-white focus:outline-none focus:border-[#358ECD]"
                                        >
                                            <option value="now">Send Now</option>
                                            <option value="schedule">Schedule</option>
                                        </select>
                                    </label>
                                    {action === 'schedule' && (
                                        <ScheduleForm value={schedule} onChange={setSchedule} />
                                    )}
                                </div>
                            </div>

                            {/* RIGHT: Parameter Configuration */}
                            <div className="border-l border-gray-200 md:pl-6 mt-5 md:mt-0">
                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-4">Parameter Configuration</h3>
                                {(!parameters || parameters.length === 0) && (
                                    <p className="text-xs text-[#4D4D4D] italic">
                                        No parameters selected in the report yet.
                                    </p>
                                )}
                                {(parameters || []).map((p) => (
                                    <div key={p.id} className="mb-4 border-b pb-4">
                                        <p className="text-xs text-[#4D4D4D] font-semibold common-size15">
                                            {p.siteName ? `${p.siteName} | ` : ''}{p.deviceName ? `${p.deviceName} | ` : ''}{p.name}{p.unit ? ` (${p.unit})` : ''}
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                            <label className="block">
                                                <span className="common-size15 text-[#4D4D4D]">Standard Name</span>
                                                <select
                                                    value={mapping[p.id]?.standard_name || GLOS_STANDARD_NAMES[0]}
                                                    onChange={(e) => updateMap(p.id, { standard_name: e.target.value })}
                                                    // `title` surfaces the full standard name on hover — the
                                                    // dropdown itself is too narrow to show the whole GLOS
                                                    // vocabulary name (e.g. `sea_surface_wave_maximum_height`)
                                                    // without truncation.
                                                    title={mapping[p.id]?.standard_name || GLOS_STANDARD_NAMES[0]}
                                                    className={`${ExpInputCls} bg-white`}>
                                                    {GLOS_STANDARD_NAMES.map((s) => <option key={s} value={s} title={s}>{s}</option>)}
                                                </select>
                                            </label>
                                            <label className="block">
                                                <span className="common-size15 text-[#4D4D4D]">Depth Value</span>
                                                <select
                                                    value={mapping[p.id]?.depth || 'None'}
                                                    onChange={(e) => updateMap(p.id, { depth: e.target.value })}
                                                    className={`${ExpInputCls} bg-white`}>
                                                    {GLOS_DEPTH_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="mx-5 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>GLOS rejected the submission: <span className="font-semibold">{error}</span></span>
                                </div>
                                {/* Permission errors from GLOS look identical to a generic API rejection —
                                    surface the most-likely cause inline so testers know whether to
                                    re-check inputs vs. coordinate with GLOS staff. */}
                                {/access|authoriz/i.test(error) && (
                                    <p className="mt-1 ml-6 text-xs text-red-600">
                                        This usually means the API key configured server-side isn't authorized
                                        to log data to this platform on GLOS's side. Have a GLOS admin grant
                                        write access for the key, or swap to a key that already has the
                                        right permissions.
                                    </p>
                                )}
                            </div>
                        )}

                        

                        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50 rounded-b-lg">
                            <button type="button" onClick={onClose} disabled={submitting}
                                className="common-size15 px-4 py-1.5 text-sm text-[#4D4D4D] border rounded-lg hover:underline disabled:opacity-50">
                                Cancel
                            </button>
                            <button type="submit" disabled={submitting}
                                className="common-size15 px-5 py-1.5 bg-[#358ECD] text-white text-sm font-semibold rounded-lg hover:bg-[#2A78B0] disabled:opacity-50 inline-flex items-center gap-1">
                                {submitting && <Loader className="w-3.5 h-3.5 animate-spin" />}
                                {action === 'schedule' ? 'Schedule' : 'Share'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

// ─── NDBC XML export dialog (minimal stub) ────────────────────────────────────
// ─── NDBC XML export dialog ───────────────────────────────────────────────────
// Two-column form mirroring the XD reference exactly.
//
//   LEFT
//     General — Name, Description, Station
//     NDBC Global Tag — 7 checkbox+text pairs (fm64ii, fm64xx, ff64k1,
//       ff64k2, fm64k3, fm64k4, fm64k6). The text input next to each
//       checkbox carries the tag value the user wants embedded in the
//       bulletin; the checkbox decides whether it's emitted.
//     Date Range Configuration — Timezone, Date Range, Time Sort.
//   RIGHT
//     Parameter Configuration — one row per report parameter, each with
//       Tag (loaded from the NDBCTag table on the server), Depth Tag,
//       and Depth Value dropdowns.
//
// Submit POSTs to /api/data/reports/<id>/export/ndbc/; the backend echoes
// the captured configuration until the real NDBC bulletin pipeline lands.
const NDBC_GLOBAL_TAG_KEYS = ['fm64ii', 'fm64xx', 'ff64k1', 'ff64k2', 'fm64k3', 'fm64k4', 'fm64k6']
const NDBC_DEPTH_TAGS    = ['None', 'depth1', 'depth2', 'depth3', 'depth4']
const NDBC_DEPTH_VALUES  = ['None', '0', '0.5', '1', '2', '3', '5', '10']

function NdbcXmlExportDialog({ reportId, reportName, parameters, reportTzLabel, onClose }) {
    const customerTz = resolveIanaTz(useAuthStore(s => s.user?.timezone))
    const [general, setGeneral] = useState({
        name:        reportName || '',
        description: '',
        station:     '',
    })
    // Global tags: { fm64ii: { enabled: false, value: '' }, … }
    const [globalTags, setGlobalTags] = useState(() => {
        const init = {}
        for (const k of NDBC_GLOBAL_TAG_KEYS) init[k] = { enabled: false, value: '' }
        return init
    })
    const [dateCfg, setDateCfg] = useState({
        // Default the export timezone to the report's own timezone setting.
        timezone:  (reportTzLabel && reportTzLabel !== 'Use account timezone') ? reportTzLabel : '(UTC+00:00) UTC',
        dateRange: 'Today',
        timeSort:  'Ascending',
    })
    // FTP destination — NDBC provisions an FTP endpoint per station where
    // XML bulletins are dropped. When populated, the backend uploads;
    // when left blank, the backend returns the XML for hand inspection.
    const [ftp, setFtp] = useState({
        host:      '',
        port:      '21',
        user:      '',
        password:  '',
        directory: '',
        protocol:  'ftp',
    })
    // Per-parameter mapping — keyed by parameter id.
    const [mapping, setMapping] = useState(() => {
        const init = {}
        for (const p of parameters || []) {
            init[p.id] = { tag: '', depth_tag: 'None', depth_value: 'None' }
        }
        return init
    })
    // NDBC tag catalogue, loaded once when the modal opens.
    const [tagOptions, setTagOptions] = useState([])
    useEffect(() => {
        let cancelled = false
        listNdbcTags()
            .then((rows) => { if (!cancelled) setTagOptions(rows) })
            .catch(() => { if (!cancelled) setTagOptions([]) })
        return () => { cancelled = true }
    }, [])

    const [submitting, setSubmitting] = useState(false)
    const [done,       setDone]       = useState(false)
    const [error,      setError]      = useState(null)
    // Server response — for the "no FTP → return XML" path we surface
    // the generated XML so testers can copy it out and hand-upload.
    const [result,     setResult]     = useState(null)
    const [action,     setAction]     = useState('now')     // 'now' | 'schedule'
    const [schedule,   setSchedule]   = useState(emptyScheduleValue)

    const updateGlobalTag = (k, patch) =>
        setGlobalTags((g) => ({ ...g, [k]: { ...(g[k] || {}), ...patch } }))
    const updateMap = (pid, patch) =>
        setMapping((m) => ({ ...m, [pid]: { ...(m[pid] || {}), ...patch } }))

    const submit = async (e) => {
        e?.preventDefault?.()
        if (!general.station.trim()) { setError('Station is required.'); return }
        if (!reportId) { setError('Save the report first before exporting.'); return }
        setSubmitting(true); setError(null)
        try {
            const body = {
                general: {
                    name:        general.name,
                    description: general.description,
                    station:     general.station.trim(),
                },
                global_tags: globalTags,
                date_range:  dateCfg,
                parameter_mappings: (parameters || []).map((p) => ({
                    parameter_id:   p.id,
                    parameter_name: p.name,
                    parameter_unit: p.unit || '',
                    tag:            mapping[p.id]?.tag         || '',
                    depth_tag:      mapping[p.id]?.depth_tag   || 'None',
                    depth_value:    mapping[p.id]?.depth_value || 'None',
                })),
                // Only include the FTP block if the user actually filled in
                // a host — otherwise the backend returns the built XML
                // instead of uploading, useful for setup / verification.
                ...((ftp.host || '').trim() ? { ftp: {
                    host:      ftp.host.trim(),
                    port:      Number(ftp.port) || (ftp.protocol === 'sftp' ? 22 : 21),
                    user:      ftp.user.trim(),
                    password:  ftp.password,
                    directory: ftp.directory.trim(),
                    protocol:  ftp.protocol || 'ftp',
                }} : {}),
            }
            if (action === 'schedule') {
                const resp = await createSchedule(reportId, {
                    transport:     'ndbc',
                    enabled:       !!schedule.enabled,
                    only_new_data: !!schedule.only_new_data,
                    days_of_week:  schedule.days_of_week,
                    schedule_type: schedule.schedule_type,
                    send_at:       schedule.send_at,
                    timezone:      customerTz,
                    config:        body,   // full NDBC payload stored verbatim
                })
                setResult({ scheduled: true, schedule_id: resp?.id, next_run_at: resp?.next_run_at })
            } else {
                const resp = await exportReportNdbc(reportId, body)
                setResult(resp || {})
            }
            setDone(true)
        } catch (e2) {
            setError(e2?.response?.data?.message || e2?.message || 'Failed to export NDBC XML')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        // No backdrop onClick — see the Share dialog above. The NDBC form is the
        // longest of the three (per-parameter tags, global tags, FTP creds), so
        // discarding it on an outside click is the most costly of all.
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 no-print">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Share Report: NDBC XML</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>

                {done ? (
                    <div className="px-5 py-6 text-sm text-[#4D4D4D] flex-1 overflow-y-auto">
                        {result?.scheduled ? (
                            <>
                                <div className="inline-flex items-center gap-2 text-green-700 mb-2">
                                    <Check className="w-4 h-4" /> Schedule saved.
                                </div>
                                <p className="text-xs text-gray-600">
                                    Next run:{' '}
                                    <span className="">
                                        {result?.next_run_at || 'no valid day/time picked — enable a day and add a time slot to activate.'}
                                    </span>
                                </p>
                                <p className="text-xs text-[#4D4D4D] mt-1">
                                    Manage all schedules from the <strong>View All</strong> button on the Report toolbar.
                                </p>
                            </>
                        ) : result?.uploaded ? (
                            <>
                                <div className="inline-flex items-center gap-2 text-green-700 mb-2">
                                    <Check className="w-4 h-4" /> Uploaded to NDBC FTP.
                                </div>
                                <ul className="mt-2 text-xs text-[#4D4D4D] space-y-0.5">
                                    <li><span className="text-[#4D4D4D]">Server:</span> {result.upload?.ftp_host}</li>
                                    <li><span className="text-[#4D4D4D]">Directory:</span> {result.upload?.ftp_directory || '/'}</li>
                                    <li><span className="text-[#4D4D4D]">File:</span> <code>{result.upload?.filename || result?.filename}</code></li>
                                    <li><span className="text-[#4D4D4D]">Size:</span> {result.upload?.bytes_uploaded} bytes</li>
                                    <li><span className="text-[#4D4D4D]">Protocol:</span> {(result.upload?.protocol || 'ftp').toUpperCase()}</li>
                                </ul>
                                {result?.xml && (
                                    <div className="mt-3">
                                        <div className="flex items-center gap-3 mb-2">
                                            <button
                                                type="button"
                                                onClick={() => downloadBlob(result?.filename || result.upload?.filename || 'ndbc.xml', result.xml, 'application/octet-stream')}
                                                className="px-3 py-1 bg-[#358ECD] text-white text-xs rounded hover:bg-[#2A78B0]"
                                            >
                                                Download XML
                                            </button>
                                            <span className="text-xs text-amber-600">⚠ Open with a text editor — NDBC XML cannot be viewed in a browser</span>
                                        </div>
                                        <textarea
                                            readOnly
                                            value={result.xml}
                                            className="w-full h-48 text-xs font-mono border border-gray-200 rounded p-2"
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="inline-flex items-center gap-2 text-blue-700 mb-1">
                                    <Check className="w-4 h-4" /> XML generated.
                                </div>
                                <p className="mb-2">FTP destination wasn't provided. Download the file to inspect or hand-upload it:</p>
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="text-xs text-[#4D4D4D]">Filename: <code>{result?.filename}</code></span>
                                    <button
                                        type="button"
                                        onClick={() => downloadBlob(result?.filename || 'ndbc.xml', result?.xml || '', 'application/octet-stream')}
                                        className="px-3 py-1 bg-[#358ECD] text-white text-xs rounded hover:bg-[#2A78B0]"
                                    >
                                        Download XML
                                    </button>
                                    <span className="text-xs text-amber-600">⚠ Open with a text editor — NDBC XML cannot be viewed in a browser</span>
                                </div>
                                <textarea
                                    readOnly
                                    value={result?.xml || ''}
                                    className="w-full h-64 text-xs font-mono border border-gray-200 rounded p-2"
                                />
                            </>
                        )}
                        <div className="mt-6 text-right">
                            <button onClick={onClose} className="px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded hover:bg-[#2A78B0]">OK</button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="flex-1 min-h-0 overflow-y-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 px-8 py-6">
                            {/* LEFT column */}
                            <div>
                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3">General</h3>
                                <ExpLabel>
                                    <input type="text" value={general.name}
                                        onChange={(e) => setGeneral((g) => ({ ...g, name: e.target.value }))}
                                        placeholder="Name" className={ExpInputCls} />
                                </ExpLabel>
                                <ExpLabel>
                                    <textarea rows={3} value={general.description}
                                        onChange={(e) => setGeneral((g) => ({ ...g, description: e.target.value }))}
                                        placeholder="Description"
                                        className={`${ExpInputCls} resize-y`} />
                                </ExpLabel>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-xs text-[#4D4D4D] w-16">Station</span>
                                    <input type="text" value={general.station}
                                        onChange={(e) => setGeneral((g) => ({ ...g, station: e.target.value }))}
                                        placeholder="Station"
                                        className={`${ExpInputCls} mt-0 flex-1`} />
                                </div>

                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mt-5 mb-3 border-t pt-4">NDBC Global Tag</h3>
                                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                                    {NDBC_GLOBAL_TAG_KEYS.map((k) => {
                                        const row = globalTags[k] || { enabled: false, value: '' }
                                        return (
                                            <div key={k}>
                                                <label className="common-size15 flex items-center gap-2 text-xs text-[#4D4D4D] mb-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!row.enabled}
                                                        onChange={(e) => updateGlobalTag(k, { enabled: e.target.checked })}
                                                    />
                                                    Enable &lt;{k}&gt; tag
                                                </label>
                                                <input
                                                    type="text"
                                                    value={row.value}
                                                    onChange={(e) => updateGlobalTag(k, { value: e.target.value })}
                                                    disabled={!row.enabled}
                                                    className={`${ExpInputCls} disabled:opacity-50`}
                                                />
                                            </div>
                                        )
                                    })}
                                </div>

                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mt-5 mb-3 border-t pt-4">FTP Destination</h3>
                                <p className="text-[11px] text-[#4D4D4D] mb-2">
                                    Credentials NDBC provisions for this station. Leave blank to preview the
                                    generated XML without uploading.
                                </p>
                                <label className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">Host</span>
                                    <input type="text" value={ftp.host}
                                        onChange={(e) => setFtp((f) => ({ ...f, host: e.target.value }))}
                                        placeholder="ftpingest.ndbc.noaa.gov"
                                        className={ExpInputCls} />
                                </label>
                                <label className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">Port</span>
                                    <input type="number" value={ftp.port}
                                        onChange={(e) => setFtp((f) => ({ ...f, port: e.target.value }))}
                                        className={ExpInputCls} />
                                </label>
                                <label className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">User</span>
                                    <input type="text" value={ftp.user}
                                        onChange={(e) => setFtp((f) => ({ ...f, user: e.target.value }))}
                                        className={ExpInputCls} />
                                </label>
                                <label className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">Password</span>
                                    <input type="password" value={ftp.password}
                                        onChange={(e) => setFtp((f) => ({ ...f, password: e.target.value }))}
                                        className={ExpInputCls} />
                                </label>
                                <label className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">Directory (optional)</span>
                                    <input type="text" value={ftp.directory}
                                        onChange={(e) => setFtp((f) => ({ ...f, directory: e.target.value }))}
                                        placeholder="/incoming"
                                        className={ExpInputCls} />
                                </label>
                                <label className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">Protocol</span>
                                    <select
                                        value={ftp.protocol || 'ftp'}
                                        onChange={(e) => {
                                            const proto = e.target.value
                                            setFtp((f) => {
                                                const defaultPort = proto === 'sftp' ? '22' : '21'
                                                const port = (f.port === '21' || f.port === '22' || f.port === '')
                                                    ? defaultPort : f.port
                                                return { ...f, protocol: proto, port }
                                            })
                                        }}
                                        className={`${ExpInputCls} bg-white cstm-br`}
                                    >
                                        <option value="ftp">FTP (port 21)</option>
                                        <option value="ftps">FTPS — Explicit TLS (port 21)</option>
                                        <option value="sftp">SFTP (SSH — port 22)</option>
                                    </select>
                                </label>

                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mt-5 mb-3 border-t pt-4">Date Range Configuration</h3>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 text-xs font-semibold text-right cstm-w text-[#4D4D4D] w-30">Timezone</span>
                                    <select value={dateCfg.timezone}
                                        onChange={(e) => setDateCfg((d) => ({ ...d, timezone: e.target.value }))}
                                        className={`${ExpInputCls} bg-white mt-0 flex-1 cstm-br`}>
                                        {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 text-xs font-semibold text-right cstm-w text-[#4D4D4D] w-30">Date Range</span>
                                    <select value={dateCfg.dateRange}
                                        onChange={(e) => setDateCfg((d) => ({ ...d, dateRange: e.target.value }))}
                                        className={`${ExpInputCls} bg-white mt-0 flex-1 cstm-br`}>
                                        {DATE_RANGE_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="common-size15 text-xs font-semibold text-right cstm-w text-[#4D4D4D] w-30">Time Sort</span>
                                    <select value={dateCfg.timeSort}
                                        onChange={(e) => setDateCfg((d) => ({ ...d, timeSort: e.target.value }))}
                                        className={`${ExpInputCls} bg-white mt-0 flex-1 cstm-br`}>
                                        <option>Ascending</option>
                                        <option>Descending</option>
                                    </select>
                                </div>
                                {/* Action — Send Now or Schedule recurring uploads. */}
                                <div className="text-sm mt-5 mb-3 border-t pt-4">
                                    <label className="block text-sm">
                                        <span className="common-size18 text-xs font-bold text-[#4D4D4D] block mb-2">Action</span>
                                        <select
                                            value={action}
                                            onChange={(e) => setAction(e.target.value)}
                                            className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded bg-white focus:outline-none focus:border-[#358ECD]"
                                        >
                                            <option value="now">Send Now</option>
                                            <option value="schedule">Schedule</option>
                                        </select>
                                    </label>
                                    {action === 'schedule' && (
                                        <ScheduleForm value={schedule} onChange={setSchedule} />
                                    )}
                                </div>
                            </div>

                            {/* RIGHT column — Parameter Configuration */}
                            <div className="border-l border-gray-200 md:pl-6 mt-5 md:mt-0">
                                <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-4">Parameter Configuration</h3>
                                {(!parameters || parameters.length === 0) && (
                                    <p className="text-xs text-[#4D4D4D] italic font-semibold common-size15">No parameters selected in the report yet.</p>
                                )}
                                {(parameters || []).map((p) => (
                                    <div key={p.id} className="mb-4 border-b pb-4">
                                        <p className="text-xs text-[#4D4D4D] font-semibold common-size15">
                                            {p.siteName ? `${p.siteName} | ` : ''}{p.deviceName ? `${p.deviceName} | ` : ''}{p.name}{p.unit ? ` (${p.unit})` : ''}
                                        </p>
                                        <div className="grid grid-cols-3 gap-2 mt-3">
                                            <label className="block">
                                                <span className="common-size15 text-[#4D4D4D]">Tag</span>
                                                <select
                                                    value={mapping[p.id]?.tag || ''}
                                                    onChange={(e) => updateMap(p.id, { tag: e.target.value })}
                                                    className={`${ExpInputCls} bg-white`}>
                                                    <option value="">—</option>
                                                    {tagOptions.map((t) => (
                                                        <option key={t.id} value={t.name} title={t.description || ''}>
                                                            {t.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="block">
                                                <span className="common-size15 text-[#4D4D4D]">Depth Tag</span>
                                                <select
                                                    value={mapping[p.id]?.depth_tag || 'None'}
                                                    onChange={(e) => updateMap(p.id, { depth_tag: e.target.value })}
                                                    className={`${ExpInputCls} bg-white`}>
                                                    {NDBC_DEPTH_TAGS.map((d) => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                            </label>
                                            <label className="block">
                                                <span className="common-size15 text-[#4D4D4D]">Depth Value</span>
                                                <select
                                                    value={mapping[p.id]?.depth_value || 'None'}
                                                    onChange={(e) => updateMap(p.id, { depth_value: e.target.value })}
                                                    className={`${ExpInputCls} bg-white`}>
                                                    {NDBC_DEPTH_VALUES.map((d) => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="mx-5 mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" /> {error}
                            </div>
                        )}

                        

                        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50 rounded-b-lg">
                            <button type="button" onClick={onClose} disabled={submitting}
                                className="common-size15 px-4 py-1.5 text-sm border rounded-lg text-[#4D4D4D] hover:underline disabled:opacity-50">
                                Cancel
                            </button>
                            <button type="submit" disabled={submitting}
                                className="common-size15 px-4 py-1.5 bg-[#358ECD] text-white text-sm font-semibold rounded-lg hover:bg-[#2A78B0] disabled:opacity-50 inline-flex items-center gap-1">
                                {submitting && <Loader className="w-3.5 h-3.5 animate-spin" />}
                                {action === 'schedule' ? 'Schedule' : 'Share'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

// Shared input styling for the Export dialogs. Inlined here so other
// places don't accidentally pick them up.
const ExpInputCls = 'mt-1 w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#358ECD]'
function ExpLabel({ label, children }) {
    return (
        <label className="flex items-center gap-2 mb-3">
            {label && <span className="common-size15 font-semibold text-right cstm-w text-[#4D4D4D]">{label}</span>}
            {children}
        </label>
    )
}
